import {
  FormEvent,
  type PointerEvent as ReactPointerEvent,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import {
  APP_HELP_DESTINATIONS,
  ASSISTANT_PROPOSAL_CONTRACT_VERSION,
  TransactionType,
  type AiActionProposal,
  type AiActionProposalStatus,
  type AiActionProposalType,
  type AppHelpDeepLink,
  type AssistantAnalysisCard,
  type AssistantAccessSource,
  type AssistantMessage,
  type BillingEntitlementResponse,
  type InsightsSummary,
} from '@financial/shared';
import { api } from '../lib/api';
import {
  cancelAssistantProposal,
  confirmAssistantProposal,
  createAssistantConversation,
  getAssistantCapabilities,
  getAssistantConversation,
  getAssistantProposal,
  listAssistantConversations,
  listAssistantProposals,
  sendAssistantMessage,
} from '../features/assistant/api';
import { getApiErrorMessage } from '../lib/errors';
import { useInsights } from '../features/insights/useInsights';
import { formatMoney } from '../lib/format';
import { useAuthStore } from '../store/auth';
import { SectionGuideButton } from '../components/onboarding/SectionGuide';
import { Tooltip } from '../components/ui/Tooltip';

const ASSISTANT_CAPABILITIES_REFRESH_MS = 30_000;

const SUGGESTIONS = [
  {
    label: 'Registrar una compra',
    description: 'Dime qué pasó; completo el registro y tú lo revisas.',
    prompt:
      'Registra esta compra: ayer gasté $120 en comida rápida con mi tarjeta de crédito, en una sola exhibición.',
    editableExample: '$120',
    icon: 'card',
    kind: 'action',
    usesConsultation: true,
    requiredAction: 'create_card_charge',
  },
  {
    label: '¿Puedo pagarlo?',
    description: 'Revisa una compra con tus números.',
    prompt: '¿Puedo pagar una compra de $5,000 a 6 meses sin intereses sin desordenar mi mes?',
    editableExample: '$5,000',
    icon: 'wallet',
    kind: 'analysis',
  },
  {
    label: 'Ayúdame a decidir',
    description: 'Piensa si de verdad te conviene comprarlo.',
    prompt:
      'Ayúdame a decidir si vale la pena comprar algo de $5,000. Quiero pensar si de verdad lo necesito.',
    editableExample: '$5,000',
    icon: 'target',
    kind: 'analysis',
  },
  {
    label: 'Configurar mi tarjeta',
    description: 'Descríbela; completo los datos y tú decides si guardarla.',
    prompt:
      'Configura mi tarjeta de crédito Nu morada: la emitió Nu, tiene un límite de $30,000, debo $5,000, corta el día 15 y vence el día 5.',
    editableExample: undefined,
    icon: 'card',
    kind: 'action',
    usesConsultation: true,
    requiredAction: 'configure_credit_card',
  },
  {
    label: 'Agregar una cuenta',
    description: 'Descríbela; preparo los datos y tú decides si guardarla.',
    prompt: 'Quiero agregar una nueva cuenta para registrar el dinero que tengo.',
    editableExample: undefined,
    icon: 'wallet',
    kind: 'action',
    usesConsultation: true,
    requiredAction: 'create_account',
  },
  {
    label: 'Registrar un pago',
    description: 'Cuéntame cómo pagaste; preparo el registro para revisarlo.',
    prompt: 'Registra el pago de $500 que hice hoy a mi tarjeta desde mi cuenta de nómina.',
    editableExample: undefined,
    icon: 'card',
    kind: 'action',
    usesConsultation: true,
    requiredAction: 'create_card_payment',
  },
  {
    label: 'Registrar una compra anterior',
    description: 'Agrega una compra a meses que ya empezaste a pagar.',
    prompt:
      'Compré un celular de $14,999 a 12 meses sin intereses y voy en el pago 3 de 12. Quiero registrarlo en mi tarjeta.',
    editableExample: '$14,999',
    icon: 'card',
    kind: 'action',
    usesConsultation: true,
    requiredAction: 'create_card_charge',
  },
  {
    label: '¿Cuánto debo en mi tarjeta de crédito?',
    description: 'Consulta la deuda que registraste.',
    prompt: '¿Cuánto debo en mi tarjeta de crédito?',
    editableExample: undefined,
    icon: 'card',
    kind: 'analysis',
  },
  {
    label: '¿Qué pagos vienen pronto?',
    description: 'Anticipa tus compromisos registrados.',
    prompt: '¿Qué pagos debo cubrir próximamente?',
    editableExample: undefined,
    icon: 'calendar',
    kind: 'analysis',
  },
] as const satisfies ReadonlyArray<{
  label: string;
  description: string;
  prompt: string;
  editableExample?: string;
  icon: 'wallet' | 'target' | 'card' | 'calendar';
  kind: 'analysis' | 'action';
  usesConsultation?: true;
  requiredAction?: AiActionProposalType;
}>;

interface AssistantSendAttempt {
  message: string;
  requestId: string;
}

function newAssistantRequestId(): string {
  if (typeof globalThis.crypto.randomUUID === 'function') {
    return globalThis.crypto.randomUUID();
  }
  const bytes = globalThis.crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

export function AssistantPage() {
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [startingNewConversation, setStartingNewConversation] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [text, setText] = useState('');
  const [lastAttempt, setLastAttempt] = useState<AssistantSendAttempt | null>(null);
  const [localMessages, setLocalMessages] = useState<AssistantMessage[]>([]);
  const [proposalUpdates, setProposalUpdates] = useState<Record<string, AiActionProposal>>({});
  const [recentProposalResult, setRecentProposalResult] = useState<AiActionProposal | null>(null);
  const [proposalNow, setProposalNow] = useState(() => Date.now());
  const [liveAnnouncement, setLiveAnnouncement] = useState('');
  const [showJumpToLatest, setShowJumpToLatest] = useState(false);
  const threadRef = useRef<HTMLDivElement>(null);
  const threadContentRef = useRef<HTMLDivElement>(null);
  const composerInputRef = useRef<HTMLTextAreaElement>(null);
  const shouldStickToLatestRef = useRef(true);
  const proposalActionLocked = useRef(false);
  const queryClient = useQueryClient();
  const userId = useAuthStore((state) => state.user?.id);
  const aiConsentGranted = useAuthStore((state) => state.user?.aiConsentGranted === true);
  const meter = useQuery({
    queryKey: ['billing', 'entitlement'],
    queryFn: async () => (await api.get<BillingEntitlementResponse>('/billing/entitlement')).data,
    retry: false,
  });
  const assistantMeter = meter.data?.meters.find((item) => item.type === 'assistant_text');
  const trialExpiresAt = meter.data?.trialExpiresAt;
  const assistantAccessSource: AssistantAccessSource =
    meter.data?.assistantAccessSource ??
    (assistantMeter?.enabled ? (trialExpiresAt ? 'trial' : 'entitlement') : 'none');
  const authoritativeAccessExpiresAt = meter.data?.assistantAccessExpiresAt;
  const assistantAccessExpiresAt =
    authoritativeAccessExpiresAt ?? (assistantAccessSource === 'trial' ? trialExpiresAt : null);
  const entitlementEvaluatedAt = meter.data?.evaluatedAt;
  const entitlementEvaluationTime = entitlementEvaluatedAt
    ? Date.parse(entitlementEvaluatedAt)
    : Number.NaN;
  const assistantAccessExpiryTime = assistantAccessExpiresAt
    ? Date.parse(assistantAccessExpiresAt)
    : Number.NaN;
  const assistantAccessDurationMs =
    Number.isFinite(assistantAccessExpiryTime) && Number.isFinite(entitlementEvaluationTime)
      ? assistantAccessExpiryTime - entitlementEvaluationTime
      : Number.NaN;
  const accessEvaluationKey =
    assistantAccessExpiresAt && entitlementEvaluatedAt
      ? `${assistantAccessExpiresAt}:${entitlementEvaluatedAt}`
      : null;
  const [expiredAccessEvaluation, setExpiredAccessEvaluation] = useState<string | null>(null);
  const assistantAccessExpired =
    Number.isFinite(assistantAccessDurationMs) &&
    (assistantAccessDurationMs <= 0 || expiredAccessEvaluation === accessEvaluationKey);
  const assistantAccessEnabled = assistantMeter?.enabled === true && !assistantAccessExpired;
  const trialExpiryTime = trialExpiresAt ? Date.parse(trialExpiresAt) : Number.NaN;
  const expiredTrialAt =
    aiConsentGranted &&
    assistantMeter?.enabled === false &&
    Number.isFinite(trialExpiryTime) &&
    Number.isFinite(entitlementEvaluationTime) &&
    trialExpiryTime <= entitlementEvaluationTime
      ? trialExpiresAt
      : null;
  const assistantReady = assistantAccessEnabled && aiConsentGranted;
  const canUseAnalysis = assistantReady && (assistantMeter?.available ?? 0) > 0;
  useEffect(() => {
    if (
      !accessEvaluationKey ||
      !Number.isFinite(assistantAccessDurationMs) ||
      assistantAccessDurationMs <= 0
    ) {
      return;
    }
    const deadline = performance.now() + assistantAccessDurationMs;
    let timeoutId: number | undefined;
    const scheduleExpiryCheck = () => {
      const remaining = deadline - performance.now();
      if (remaining <= 0) {
        setExpiredAccessEvaluation(accessEvaluationKey);
        void queryClient.invalidateQueries({ queryKey: ['billing', 'entitlement'] });
        return;
      }
      timeoutId = window.setTimeout(scheduleExpiryCheck, Math.min(Math.max(remaining, 1), 60_000));
    };
    scheduleExpiryCheck();
    return () => {
      if (timeoutId !== undefined) window.clearTimeout(timeoutId);
    };
  }, [accessEvaluationKey, assistantAccessDurationMs, queryClient]);
  const capabilities = useQuery({
    queryKey: ['assistant', 'capabilities', userId],
    queryFn: getAssistantCapabilities,
    enabled: Boolean(userId && assistantReady),
    staleTime: 0,
    retry: 1,
    refetchInterval: ASSISTANT_CAPABILITIES_REFRESH_MS,
    refetchOnWindowFocus: 'always',
    refetchOnReconnect: 'always',
  });
  // Ante un error o una versión inesperada ocultamos todas las acciones. Las
  // preguntas de análisis siguen disponibles y el servidor conserva el kill switch.
  const verifiedCapabilities = capabilities.isError ? undefined : capabilities.data;
  const guidedRegistration = verifiedCapabilities?.guidedPurchaseRegistration;
  const confirmableActions =
    verifiedCapabilities?.proposalContractVersion === ASSISTANT_PROPOSAL_CONTRACT_VERSION
      ? verifiedCapabilities.confirmableActions.filter(
          (action) =>
            guidedRegistration?.enabled === true ||
            (action !== 'create_transaction' && action !== 'create_card_charge'),
        )
      : [];
  const hasConfirmableActions = confirmableActions.length > 0;
  const assistantHelpItems = [
    'Explicar tus números y los pagos que registraste.',
    'Ayudarte a pensar si una compra cabe en tu mes y si realmente te conviene.',
    ...(guidedRegistration?.enabled
      ? [
          'Preparar una compra con una frase: completa lo que ya sabe y pregunta sólo lo que falta.',
          ...(guidedRegistration.resolvesCategories
            ? [
                guidedRegistration.canCreateCategories
                  ? 'Sugerir una categoría y, si no existe, crearla junto con la compra después de que confirmes.'
                  : 'Sugerir una categoría de las que ya tienes.',
              ]
            : []),
          ...(guidedRegistration.requiresConfirmation
            ? ['Mostrarte qué cambiará y esperar tu confirmación antes de guardar.']
            : []),
        ]
      : []),
    ...(confirmableActions.includes('configure_credit_card')
      ? ['Guiarte para configurar tu primera tarjeta de crédito.']
      : []),
    ...(confirmableActions.includes('create_card_payment')
      ? ['Registrar un pago que ya hiciste a tu tarjeta de crédito.']
      : []),
    ...(confirmableActions.includes('create_account')
      ? ['Preparar una cuenta nueva y mostrarte sus datos antes de crearla.']
      : []),
  ];
  const assistantReadyRef = useRef(assistantReady);
  assistantReadyRef.current = assistantReady;
  const insights = useInsights();
  const conversation = useQuery({
    queryKey: ['assistant', userId, conversationId],
    queryFn: () => getAssistantConversation(conversationId!),
    enabled: Boolean(userId && conversationId && assistantReady),
    staleTime: 0,
    retry: false,
  });
  const history = useQuery({
    queryKey: ['assistant', 'conversations', userId],
    queryFn: listAssistantConversations,
    enabled: Boolean(userId && assistantReady),
    retry: false,
  });
  const proposals = useQuery({
    queryKey: ['assistant', 'proposals', userId],
    queryFn: listAssistantProposals,
    enabled: Boolean(userId && assistantReady),
    staleTime: 0,
    retry: false,
  });
  const rememberProposal = (proposal: AiActionProposal) => {
    setProposalUpdates((current) => ({ ...current, [proposal.id]: proposal }));
  };
  const create = useMutation({ mutationFn: createAssistantConversation });
  const send = useMutation({
    mutationFn: async (attempt: AssistantSendAttempt) => {
      const { message, requestId } = attempt;
      const created = conversationId ? null : await create.mutateAsync();
      const id = conversationId ?? created!.id;
      setConversationId(id);
      setStartingNewConversation(false);
      return {
        message: await sendAssistantMessage(id, { message, requestId }),
        conversationId: id,
      };
    },
    onSuccess: ({ message, conversationId: sentConversationId }) => {
      if (!assistantReadyRef.current) return;
      setLocalMessages((items) => [...items, message]);
      setLiveAnnouncement(
        message.proposal
          ? 'El asistente respondió e incluyó un registro pendiente de tu confirmación.'
          : 'El asistente respondió.',
      );
      setLastAttempt(null);
      void queryClient.invalidateQueries({ queryKey: ['assistant', userId, sentConversationId] });
      void queryClient.invalidateQueries({ queryKey: ['assistant', 'conversations'] });
      void queryClient.invalidateQueries({ queryKey: ['assistant', 'proposals'] });
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ['billing', 'entitlement'] });
    },
  });
  const proposalAction = useMutation({
    mutationFn: ({ id, action }: { id: string; action: 'confirm' | 'cancel' }) =>
      action === 'confirm' ? confirmAssistantProposal(id) : cancelAssistantProposal(id),
    onSuccess: (proposal, request) => {
      if (assistantReadyRef.current) {
        rememberProposal(proposal);
        setRecentProposalResult(proposal);
      }
      if (request.action === 'confirm') {
        void queryClient.invalidateQueries({ queryKey: ['transactions'] });
        void queryClient.invalidateQueries({ queryKey: ['credit-card'] });
        void queryClient.invalidateQueries({ queryKey: ['accounts'] });
        void queryClient.invalidateQueries({ queryKey: ['net-worth'] });
        void queryClient.invalidateQueries({ queryKey: ['insights'] });
        void queryClient.invalidateQueries({ queryKey: ['budget'] });
        void queryClient.invalidateQueries({ queryKey: ['categories'] });
      }
    },
    onError: async (_error, request) => {
      // La respuesta puede perderse después del commit. Reconciliamos por id;
      // si tampoco se puede leer, conservamos la card pendiente para que un
      // reintento idempotente determine el resultado sin duplicar el registro.
      if (!assistantReadyRef.current) return;
      try {
        const proposal = await getAssistantProposal(request.id);
        rememberProposal(proposal);
        if (proposal.status !== 'pending') setRecentProposalResult(proposal);
      } catch {
        // El error original y la propuesta preservada comunican la ambigüedad.
      }
    },
    onSettled: () => {
      proposalActionLocked.current = false;
      void queryClient.invalidateQueries({
        queryKey: ['assistant', 'capabilities', userId],
      });
      void queryClient.invalidateQueries({ queryKey: ['assistant', 'proposals'] });
      if (userId) void queryClient.invalidateQueries({ queryKey: ['assistant', userId] });
    },
  });
  // La primera lectura puede completar antes de que el POST persista el turno.
  // Fusionamos ambos orígenes para no ocultar el mensaje optimista ni la
  // respuesta recién recibida mientras React Query actualiza su caché.
  const serverMessages = assistantReady ? (conversation.data?.messages ?? []) : [];
  const messages = assistantReady
    ? [
        ...serverMessages,
        ...localMessages.filter(
          (local) =>
            !serverMessages.some(
              (server) =>
                server.id === local.id ||
                (local.id.startsWith('pending-') &&
                  server.role === local.role &&
                  server.content === local.content),
            ),
        ),
      ]
    : [];
  const proposalById = new Map<string, AiActionProposal>();
  if (assistantReady) {
    for (const message of messages) {
      if (message.proposal) proposalById.set(message.proposal.id, message.proposal);
    }
    for (const proposal of proposals.data ?? []) proposalById.set(proposal.id, proposal);
    for (const proposal of Object.values(proposalUpdates)) proposalById.set(proposal.id, proposal);
  }
  const pendingProposals = [...proposalById.values()]
    .filter(
      (proposal) =>
        proposal.status === 'pending' && new Date(proposal.expiresAt).getTime() > proposalNow,
    )
    .sort((left, right) => right.createdAt.localeCompare(left.createdAt));
  const nextProposalExpiry = pendingProposals.reduce(
    (earliest, proposal) => Math.min(earliest, new Date(proposal.expiresAt).getTime()),
    Number.POSITIVE_INFINITY,
  );
  const inlineProposalIds = new Set(
    messages.flatMap((message) => (message.proposal ? [message.proposal.id] : [])),
  );
  const pendingProposalsOutsideThread = pendingProposals.filter(
    (proposal) => !inlineProposalIds.has(proposal.id),
  );
  const trayRecentProposal =
    assistantReady && recentProposalResult && !inlineProposalIds.has(recentProposalResult.id)
      ? recentProposalResult
      : null;
  const unavailable = create.isError || send.isError;
  const status = !navigator.onLine
    ? 'offline'
    : send.isPending
      ? 'sending'
      : unavailable
        ? 'error'
        : 'ready';

  const scrollToLatest = useCallback((behavior: ScrollBehavior = 'smooth') => {
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const thread = threadRef.current;
    if (!thread) return;
    shouldStickToLatestRef.current = true;
    setShowJumpToLatest(false);
    thread.scrollTo({
      top: thread.scrollHeight,
      behavior: reducedMotion ? 'auto' : behavior,
    });
  }, []);

  const updateThreadPosition = () => {
    const thread = threadRef.current;
    if (!thread) return;
    const distanceFromLatest = thread.scrollHeight - thread.scrollTop - thread.clientHeight;
    const nearLatest = distanceFromLatest <= 80;
    shouldStickToLatestRef.current = nearLatest;
    if (nearLatest) setShowJumpToLatest(false);
  };

  useEffect(() => {
    // MongoDB es la fuente de verdad: permite retomar el chat desde otro
    // dispositivo sin conservar contenido ni identificadores en el navegador.
    if (assistantReady && !conversationId && !startingNewConversation && history.data?.[0])
      setConversationId(history.data[0].id);
  }, [assistantReady, conversationId, history.data, startingNewConversation]);
  useEffect(() => {
    if (conversation.isError && conversationId) {
      setConversationId(null);
      setLocalMessages([]);
    }
  }, [conversation.isError, conversationId]);
  useEffect(() => {
    if (!assistantReady) {
      setConversationId(null);
      setLocalMessages([]);
      setStartingNewConversation(false);
      setHistoryOpen(false);
      setText('');
      setLastAttempt(null);
      setProposalUpdates({});
      setRecentProposalResult(null);
      void queryClient.cancelQueries({ queryKey: ['assistant'] }).finally(() => {
        queryClient.removeQueries({ queryKey: ['assistant'] });
      });
    }
  }, [assistantReady, queryClient]);

  useEffect(() => {
    if (!Number.isFinite(nextProposalExpiry)) return;
    const remaining = nextProposalExpiry - Date.now();
    const timer = window.setTimeout(
      () => setProposalNow(Date.now()),
      Math.max(0, Math.min(remaining + 50, 60_000)),
    );
    return () => window.clearTimeout(timer);
  }, [nextProposalExpiry, proposalNow]);

  const resizeComposer = useCallback(() => {
    const input = composerInputRef.current;
    if (!input) return;
    input.style.height = 'auto';
    const nextHeight = text.length === 0 ? 44 : Math.max(44, Math.min(input.scrollHeight, 112));
    input.style.height = `${nextHeight}px`;
    input.style.overflowY = input.scrollHeight > 112 ? 'auto' : 'hidden';
  }, [text]);

  useLayoutEffect(() => {
    resizeComposer();
  }, [resizeComposer]);

  useEffect(() => {
    const viewport = window.visualViewport;
    window.addEventListener('resize', resizeComposer);
    viewport?.addEventListener('resize', resizeComposer);
    return () => {
      window.removeEventListener('resize', resizeComposer);
      viewport?.removeEventListener('resize', resizeComposer);
    };
  }, [resizeComposer]);

  useEffect(() => {
    const thread = threadRef.current;
    const content = threadContentRef.current;
    if (!thread || !content) return;

    const observer = new ResizeObserver(() => {
      if (shouldStickToLatestRef.current) thread.scrollTop = thread.scrollHeight;
    });
    observer.observe(thread);
    observer.observe(content);
    return () => observer.disconnect();
  }, []);

  useLayoutEffect(() => {
    const thread = threadRef.current;
    if (!thread) return;
    if (shouldStickToLatestRef.current) {
      thread.scrollTop = thread.scrollHeight;
      setShowJumpToLatest(false);
    } else {
      setShowJumpToLatest(true);
    }
  }, [messages.length, send.isPending]);

  const submit = (event?: FormEvent, suggestion?: string) => {
    event?.preventDefault();
    const message = (suggestion ?? text).trim();
    if (!message || message.length > 500 || send.isPending || !navigator.onLine || !assistantReady)
      return;
    const attempt = { message, requestId: newAssistantRequestId() };
    setLiveAnnouncement('');
    setLocalMessages((items) => [
      ...items,
      {
        id: `pending-${attempt.requestId}`,
        role: 'user',
        content: message,
        createdAt: new Date().toISOString(),
      },
    ]);
    setText('');
    setLastAttempt(attempt);
    send.mutate(attempt);
  };
  const prefillSuggestion = (suggestion: (typeof SUGGESTIONS)[number]) => {
    setText(suggestion.prompt);
    window.requestAnimationFrame(() => {
      const input = composerInputRef.current;
      input?.focus();
      if (!input || !suggestion.editableExample) return;
      const start = suggestion.prompt.indexOf(suggestion.editableExample);
      if (start >= 0) input.setSelectionRange(start, start + suggestion.editableExample.length);
    });
  };
  const retryLastAttempt = () => {
    if (!lastAttempt || send.isPending || !navigator.onLine || !assistantReady) return;
    send.mutate(lastAttempt);
  };
  const startNewConversation = () => {
    shouldStickToLatestRef.current = true;
    setShowJumpToLatest(false);
    setConversationId(null);
    setLocalMessages([]);
    setStartingNewConversation(true);
    create.reset();
    send.reset();
  };
  const selectConversation = (id: string) => {
    shouldStickToLatestRef.current = true;
    setShowJumpToLatest(false);
    setConversationId(id);
    setLocalMessages([]);
    setStartingNewConversation(false);
    setHistoryOpen(false);
  };
  const runProposalAction = (id: string, action: 'confirm' | 'cancel') => {
    const proposal = proposalById.get(id);
    if (
      proposalActionLocked.current ||
      proposal?.status !== 'pending' ||
      (action === 'confirm' && !confirmableActions.includes(proposal.actionType)) ||
      new Date(proposal.expiresAt).getTime() <= Date.now()
    )
      return;
    proposalActionLocked.current = true;
    rememberProposal(proposal);
    proposalAction.reset();
    proposalAction.mutate({ id, action });
  };
  const activeProposalId = proposalAction.isPending ? proposalAction.variables?.id : undefined;
  const activeProposalAction = proposalAction.isPending
    ? proposalAction.variables?.action
    : undefined;
  const failedProposalId =
    proposalAction.isError &&
    proposalById.get(proposalAction.variables?.id ?? '')?.status === 'pending'
      ? proposalAction.variables?.id
      : undefined;
  const proposalActionError = proposalAction.isError
    ? getApiErrorMessage(
        proposalAction.error,
        proposalAction.variables?.action === 'cancel'
          ? 'No pudimos comprobar si la propuesta se canceló. Actualiza o vuelve a intentarlo.'
          : 'No pudimos comprobar el resultado. Actualiza o vuelve a intentarlo; no se duplicará el registro.',
      )
    : undefined;

  return (
    <div className="h-full min-h-0 lg:grid lg:grid-cols-[minmax(0,1fr)_340px] lg:overflow-hidden">
      <section className="flex h-full min-h-0 flex-col bg-[#FAF8F5]">
        <header
          data-testid="assistant-header"
          className="relative shrink-0 border-b border-[#E8E2D9] bg-white px-3 pb-2.5 pt-[calc(0.625rem+env(safe-area-inset-top,0px))] sm:px-5 sm:pb-4 sm:pt-[calc(1rem+env(safe-area-inset-top,0px))] lg:px-10 lg:py-4"
        >
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <h1 className="truncate text-base font-bold tracking-tight text-slate-900 sm:text-xl">
                Mi asistente personal
              </h1>
              <p className="mt-0.5 hidden truncate text-sm text-slate-500 sm:block">
                {hasConfirmableActions
                  ? 'Pregunta con tus palabras; preparo el registro y tú decides si guardarlo.'
                  : 'Consulta tus finanzas registradas y organiza tus próximos pasos.'}
              </p>
            </div>
            <div className="flex shrink-0 items-center gap-2">
              <SectionGuideButton
                section="assistant"
                extraSection={{ title: 'Lo que puedo hacer hoy', items: assistantHelpItems }}
              />
              {assistantAccessEnabled && assistantMeter && (
                <UsageMeter
                  meter={assistantMeter}
                  accessSource={assistantAccessSource}
                  accessExpiresAt={assistantAccessExpiresAt}
                  compact
                  detailsOpen={historyOpen}
                  onShowDetails={() => setHistoryOpen(true)}
                />
              )}
              <Tooltip content="Conversaciones y opciones" side="bottom" align="end">
                <button
                  type="button"
                  onClick={() => setHistoryOpen((open) => !open)}
                  aria-expanded={historyOpen}
                  aria-haspopup="dialog"
                  aria-label="Más opciones de la conversación"
                  className="tap-target inline-flex h-11 w-11 items-center justify-center rounded-xl border border-slate-200 bg-white p-0 text-slate-700 hover:bg-slate-50"
                >
                  <MoreIcon />
                </button>
              </Tooltip>
            </div>
          </div>
          {historyOpen && (
            <ConversationMenu
              items={history.data ?? []}
              activeId={conversationId}
              pending={send.isPending}
              usageDescription={
                assistantAccessEnabled && assistantMeter
                  ? usageValidity(assistantMeter, assistantAccessSource, assistantAccessExpiresAt)
                  : undefined
              }
              onClose={() => setHistoryOpen(false)}
              onNew={() => {
                startNewConversation();
                setHistoryOpen(false);
              }}
              onSelect={selectConversation}
            />
          )}
        </header>
        <div
          ref={threadRef}
          data-testid="assistant-feed"
          onScroll={updateThreadPosition}
          role="region"
          aria-label="Conversación con el asistente"
          className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-3 py-3 sm:px-5 sm:py-5 lg:px-10 lg:py-8"
        >
          <div ref={threadContentRef} className="space-y-4 lg:space-y-5">
            {messages.length === 0 && !unavailable && assistantReady && (
              <Welcome
                period={insights.data?.month}
                onPick={prefillSuggestion}
                canUseAnalysis={canUseAnalysis}
                confirmableActions={confirmableActions}
              />
            )}
            {messages.length === 0 &&
              !unavailable &&
              !meter.isLoading &&
              !meter.isError &&
              !assistantReady && (
                <CapabilityNotice
                  consentGranted={aiConsentGranted}
                  expiredTrialAt={expiredTrialAt}
                />
              )}
            {messages.map((message) => (
              <MessageBubble
                key={message.id}
                message={message}
                proposal={
                  message.proposal
                    ? (proposalById.get(message.proposal.id) ?? message.proposal)
                    : undefined
                }
                activeProposalId={activeProposalId}
                activeProposalAction={activeProposalAction}
                failedProposalId={failedProposalId}
                proposalActionError={proposalActionError}
                confirmableActions={confirmableActions}
                onConfirm={(id) => runProposalAction(id, 'confirm')}
                onCancel={(id) => runProposalAction(id, 'cancel')}
              />
            ))}
            {status === 'sending' && <AssistantLoading />}
            {status === 'offline' && <OfflineNotice />}
            {status === 'error' && (
              <AccessNotice
                error={create.error ?? send.error}
                onRetry={retryLastAttempt}
                canRetry={Boolean(lastAttempt && navigator.onLine && assistantReady)}
              />
            )}
            {assistantAccessEnabled &&
              assistantMeter &&
              assistantMeter.limit > 0 &&
              assistantMeter.available === 0 && (
                <Status
                  tone="warning"
                  text="Ya usaste tus consultas de análisis. Aún puedes pedirme ayuda para usar la app: eso no gasta consultas. Cada consulta vuelve a estar disponible 7 días después de usarla."
                />
              )}
            {assistantReady && (
              <div className="lg:hidden">
                <PendingProposals
                  proposals={pendingProposalsOutsideThread}
                  loading={proposals.isLoading}
                  error={
                    proposals.isError
                      ? getApiErrorMessage(
                          proposals.error,
                          'No pudimos cargar tus propuestas pendientes.',
                        )
                      : undefined
                  }
                  onRetry={() => void proposals.refetch()}
                  activeProposalId={activeProposalId}
                  activeProposalAction={activeProposalAction}
                  failedProposalId={failedProposalId}
                  proposalActionError={proposalActionError}
                  confirmableActions={confirmableActions}
                  onConfirm={(id) => runProposalAction(id, 'confirm')}
                  onCancel={(id) => runProposalAction(id, 'cancel')}
                  compact
                  hideWhenEmpty
                  title="Por aprobar"
                />
              </div>
            )}
            {assistantReady && trayRecentProposal && (
              <div className="lg:hidden">
                <RecentProposalResult
                  proposal={trayRecentProposal}
                  interaction={{
                    activeProposalId,
                    activeProposalAction,
                    failedProposalId,
                    proposalActionError,
                    confirmableActions,
                    onConfirm: (id) => runProposalAction(id, 'confirm'),
                    onCancel: (id) => runProposalAction(id, 'cancel'),
                  }}
                />
              </div>
            )}
          </div>
        </div>
        <p className="sr-only" aria-live="polite" aria-atomic="true">
          {liveAnnouncement}
        </p>
        {assistantReady ? (
          <form
            data-testid="assistant-composer"
            onSubmit={(event) => submit(event)}
            className="relative shrink-0 border-t border-[#E8E2D9] bg-[#FAF8F5]/95 px-3 py-2 backdrop-blur sm:px-5 sm:py-3 lg:px-10 lg:pb-4"
          >
            {showJumpToLatest && (
              <button
                type="button"
                onClick={() => scrollToLatest('auto')}
                className="tap-target absolute bottom-[calc(100%+0.5rem)] left-1/2 z-[2] inline-flex -translate-x-1/2 items-center rounded-full border border-slate-200 bg-white px-4 text-xs font-semibold text-slate-700 shadow-lg hover:bg-slate-50"
              >
                Ir al último mensaje
              </button>
            )}
            <label className="sr-only" htmlFor="assistant-message">
              Escribe tu consulta
            </label>
            <div className="flex items-end gap-2 rounded-2xl border border-slate-300 bg-white px-2 py-1.5 shadow-sm">
              <textarea
                ref={composerInputRef}
                id="assistant-message"
                value={text}
                onChange={(event) => setText(event.target.value)}
                onKeyDown={(event) => {
                  if (event.nativeEvent.isComposing || event.keyCode === 229) return;
                  if (event.key === 'Enter' && !event.shiftKey) {
                    event.preventDefault();
                    submit();
                  }
                }}
                aria-describedby="assistant-message-limit assistant-composer-help"
                maxLength={500}
                rows={1}
                enterKeyHint="send"
                placeholder="Escribe tu pregunta…"
                className="min-h-11 min-w-0 flex-1 resize-none bg-transparent px-1.5 py-2.5 text-base leading-6 text-slate-900 outline-none placeholder:text-slate-500"
              />
              {text.length >= 400 && (
                <span
                  data-testid="assistant-counter"
                  aria-hidden="true"
                  className="mb-3 shrink-0 text-xs tabular-nums text-slate-500"
                >
                  {text.length} / 500
                </span>
              )}
              <button
                type="submit"
                disabled={!text.trim() || send.isPending || !navigator.onLine}
                aria-label="Enviar"
                className="tap-target inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-slate-900 p-0 text-white disabled:bg-slate-200 disabled:text-slate-400"
              >
                <SendIcon />
              </button>
            </div>
            <span id="assistant-message-limit" className="sr-only">
              {text.length} de 500 caracteres.
            </span>
            <p
              id="assistant-composer-help"
              className="sr-only sm:not-sr-only sm:mt-2 sm:block sm:px-1 sm:text-[11px] sm:leading-4 sm:text-stone-600"
            >
              Enter envía; Shift + Enter agrega un salto de línea.
            </p>
          </form>
        ) : (
          <AssistantAccessAction
            consentGranted={aiConsentGranted}
            expiredTrialAt={expiredTrialAt}
            loading={meter.isLoading}
            loadFailed={meter.isError}
            onRetry={() => void meter.refetch()}
          />
        )}
      </section>
      <AssistantSummary
        asideData={insights.data}
        meter={assistantAccessEnabled ? assistantMeter : undefined}
        assistantAccessEnabled={assistantAccessEnabled}
        entitlementLoadFailed={meter.isError}
        accessSource={assistantAccessSource}
        accessExpiresAt={assistantAccessExpiresAt}
        expiredTrialAt={expiredTrialAt}
        proposals={pendingProposals}
        proposalsLoading={proposals.isLoading}
        proposalsError={
          proposals.isError
            ? getApiErrorMessage(proposals.error, 'No pudimos cargar tus propuestas pendientes.')
            : undefined
        }
        onRetryProposals={() => void proposals.refetch()}
        activeProposalId={activeProposalId}
        activeProposalAction={activeProposalAction}
        failedProposalId={failedProposalId}
        proposalActionError={proposalActionError}
        confirmableActions={confirmableActions}
        onConfirmProposal={(id) => runProposalAction(id, 'confirm')}
        onCancelProposal={(id) => runProposalAction(id, 'cancel')}
        showProposals={assistantReady}
        recentProposal={trayRecentProposal}
      />
    </div>
  );
}

function ConversationMenu({
  items,
  activeId,
  pending,
  usageDescription,
  onClose,
  onNew,
  onSelect,
}: {
  items: Array<{ id: string; title: string }>;
  activeId: string | null;
  pending: boolean;
  usageDescription?: string;
  onClose: () => void;
  onNew: () => void;
  onSelect: (id: string) => void;
}) {
  const [dragStart, setDragStart] = useState<number | null>(null);
  const [dragOffset, setDragOffset] = useState(0);
  useEffect(() => {
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', closeOnEscape);
    return () => document.removeEventListener('keydown', closeOnEscape);
  }, [onClose]);
  const beginDrag = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    event.currentTarget.setPointerCapture(event.pointerId);
    setDragStart(event.clientY);
    setDragOffset(0);
  };
  const moveDrag = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (dragStart === null) return;
    setDragOffset(Math.max(0, event.clientY - dragStart));
  };
  const endDrag = (event: ReactPointerEvent<HTMLDivElement>) => {
    const finalOffset = dragStart === null ? 0 : Math.max(0, event.clientY - dragStart);
    if (finalOffset > 88) onClose();
    setDragStart(null);
    setDragOffset(0);
  };
  const content = (
    <>
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-base font-bold text-slate-900">Conversaciones</h2>
        <button
          type="button"
          onClick={onClose}
          className="tap-target text-sm font-semibold text-slate-600"
        >
          Cerrar
        </button>
      </div>
      {usageDescription && (
        <p className="mt-3 rounded-xl bg-slate-50 px-3 py-2 text-xs leading-5 text-slate-600">
          {usageDescription}
        </p>
      )}
      <button
        type="button"
        onClick={onNew}
        disabled={pending}
        className="tap-target mt-4 flex w-full items-center justify-center gap-2 rounded-xl bg-slate-900 px-4 py-3 text-sm font-semibold text-white hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-50"
      >
        <PlusIcon />
        Nueva conversación
      </button>
      <div className="mt-4 space-y-2">
        {items.map((item) => (
          <button
            type="button"
            key={item.id}
            onClick={() => onSelect(item.id)}
            className={`w-full rounded-xl border px-3 py-3 text-left text-sm ${activeId === item.id ? 'border-slate-900 bg-slate-50 font-semibold text-slate-900' : 'border-slate-200 text-slate-700 hover:bg-slate-50'}`}
          >
            {item.title}
          </button>
        ))}
        {items.length === 0 && (
          <p className="py-5 text-center text-sm text-slate-500">
            Aún no hay conversaciones guardadas.
          </p>
        )}
      </div>
    </>
  );
  return (
    <>
      <button
        type="button"
        aria-label="Cerrar opciones de conversación"
        className="fixed inset-0 z-20 hidden cursor-default lg:block"
        onClick={onClose}
      />
      <div
        className="absolute right-4 top-[calc(100%+0.5rem)] z-30 hidden w-[340px] rounded-2xl border border-slate-200 bg-white p-4 shadow-xl lg:block"
        role="dialog"
        aria-label="Opciones de conversación"
      >
        {content}
      </div>
      <div
        className="fixed inset-0 z-40 bg-slate-950/30 lg:hidden"
        role="dialog"
        aria-modal="true"
        aria-label="Opciones de conversación"
      >
        <button
          type="button"
          aria-label="Cerrar opciones"
          className="absolute inset-0 h-full w-full"
          onClick={onClose}
        />
        <section
          className="absolute inset-x-0 bottom-0 max-h-[72dvh] overflow-y-auto rounded-t-3xl bg-white px-5 pb-[calc(1.25rem+env(safe-area-inset-bottom))] pt-2 shadow-2xl"
          style={{
            transform: `translateY(${dragOffset}px)`,
            transition: dragStart === null ? 'transform 180ms ease-out' : 'none',
          }}
        >
          <div
            className="-mx-5 mb-2 flex h-8 touch-none items-center justify-center"
            onPointerDown={beginDrag}
            onPointerMove={moveDrag}
            onPointerUp={endDrag}
            onPointerCancel={endDrag}
          >
            <span className="h-1.5 w-10 rounded-full bg-slate-300" aria-hidden />
          </div>
          {content}
        </section>
      </div>
    </>
  );
}

function UsageMeter({
  meter,
  accessSource,
  accessExpiresAt,
  compact = false,
  detailsOpen = false,
  onShowDetails,
}: {
  meter: { available: number; limit: number; window: 'rolling_7d' | 'billing_period' };
  accessSource: AssistantAccessSource;
  accessExpiresAt?: string | null;
  compact?: boolean;
  detailsOpen?: boolean;
  onShowDetails?: () => void;
}) {
  const percentage =
    meter.limit > 0 ? Math.max(0, Math.min(100, (meter.available / meter.limit) * 100)) : 0;
  const validity = usageValidity(meter, accessSource, accessExpiresAt);
  if (compact)
    return (
      <Tooltip content={validity} side="bottom" align="end">
        <button
          type="button"
          onClick={onShowDetails}
          aria-expanded={detailsOpen}
          aria-haspopup="dialog"
          aria-label={`${meter.available} de ${meter.limit} consultas disponibles. ${validity} Mostrar detalle.`}
          className="tap-target inline-flex h-11 items-center gap-2 rounded-xl border border-slate-200 bg-white px-2.5 text-sm font-semibold tabular-nums text-slate-700 hover:bg-slate-50 sm:px-3"
        >
          <span className="hidden h-1.5 w-14 overflow-hidden rounded-full bg-slate-200 sm:block">
            <span
              className="block h-full rounded-full bg-green-600"
              style={{ width: `${percentage}%` }}
            />
          </span>
          <span className="sm:hidden">
            <UsageIcon />
          </span>
          <span>
            {meter.available}
            <span className="text-slate-500">/{meter.limit}</span>
            <span className="hidden sm:inline"> consultas</span>
          </span>
        </button>
      </Tooltip>
    );
  return (
    <div className="rounded-2xl border border-slate-200 p-4">
      <div className="flex items-baseline justify-between gap-3">
        <p className="text-xl font-bold tabular-nums text-slate-900">
          {meter.available}
          <span className="text-sm font-medium text-slate-500"> de {meter.limit}</span>
        </p>
        <span className="text-xs text-slate-500">disponibles</span>
      </div>
      <div className="mt-3 h-2 overflow-hidden rounded-full bg-slate-100">
        <div className="h-full rounded-full bg-green-600" style={{ width: `${percentage}%` }} />
      </div>
      <p className="mt-3 text-xs leading-5 text-slate-500">
        {validity} Si se agotan, el resto de la app sigue funcionando igual.
      </p>
    </div>
  );
}

function usageValidity(
  meter: { limit: number },
  accessSource: AssistantAccessSource,
  accessExpiresAt?: string | null,
) {
  const availability = `Puedes tener hasta ${meter.limit} consultas disponibles. Cada consulta que uses vuelve a estar disponible 7 días después.`;
  if (!accessExpiresAt) return availability;
  const labels: Partial<Record<AssistantAccessSource, string>> = {
    administrative: 'Acceso administrativo válido hasta el',
    entitlement: 'Plus vigente hasta el',
    trial: 'Prueba válida hasta el',
  };
  const label = labels[accessSource] ?? 'Acceso válido hasta el';
  return `${label} ${formatShortDate(accessExpiresAt)}. ${availability}`;
}

interface ProposalInteractionProps {
  activeProposalId?: string;
  activeProposalAction?: 'confirm' | 'cancel';
  failedProposalId?: string;
  proposalActionError?: string;
  confirmableActions: readonly AiActionProposalType[];
  onConfirm: (id: string) => void;
  onCancel: (id: string) => void;
}

function AssistantSummary({
  asideData,
  meter,
  assistantAccessEnabled,
  entitlementLoadFailed,
  accessSource,
  accessExpiresAt,
  expiredTrialAt,
  proposals,
  proposalsLoading,
  proposalsError,
  onRetryProposals,
  activeProposalId,
  activeProposalAction,
  failedProposalId,
  proposalActionError,
  confirmableActions,
  onConfirmProposal,
  onCancelProposal,
  showProposals,
  recentProposal,
}: {
  asideData: InsightsSummary | undefined;
  meter: { available: number; limit: number; window: 'rolling_7d' | 'billing_period' } | undefined;
  assistantAccessEnabled: boolean;
  entitlementLoadFailed: boolean;
  accessSource: AssistantAccessSource;
  accessExpiresAt?: string | null;
  expiredTrialAt?: string | null;
  proposals: AiActionProposal[];
  proposalsLoading: boolean;
  proposalsError?: string;
  onRetryProposals: () => void;
  activeProposalId?: string;
  activeProposalAction?: 'confirm' | 'cancel';
  failedProposalId?: string;
  proposalActionError?: string;
  confirmableActions: readonly AiActionProposalType[];
  onConfirmProposal: (id: string) => void;
  onCancelProposal: (id: string) => void;
  showProposals: boolean;
  recentProposal: AiActionProposal | null;
}) {
  const kpis = asideData?.kpis;
  const currency = asideData?.currency;
  return (
    <aside
      className="hidden overflow-y-auto border-l border-slate-200 bg-white p-[22px] lg:block"
      aria-label="Resumen y propuestas"
      tabIndex={0}
    >
      <section>
        <h2 className="text-xs font-bold uppercase tracking-wider text-slate-500">
          Resumen de este mes
        </h2>
        <div className="mt-3 divide-y divide-slate-100 rounded-2xl border border-slate-200 px-4">
          {kpis && currency ? (
            <>
              <SummaryRow
                label={
                  kpis.availableFree < 0
                    ? 'Exceso sobre tu límite mensual de gasto'
                    : 'Restante de tu límite mensual de gasto'
                }
                value={formatMoney(Math.abs(kpis.availableFree), currency)}
                tone={kpis.availableFree < 0 ? 'expense' : 'default'}
              />
              <SummaryRow
                label="Gastos del día a día"
                value={formatMoney(kpis.expenses, currency)}
                tone="warning"
              />
              <SummaryRow
                label="Estimado al final del mes"
                value={formatMoney(kpis.projectedClosing, currency)}
                tone={kpis.projectedClosing < 0 ? 'expense' : 'default'}
              />
              <SummaryRow
                label="Aportado a metas"
                value={formatMoney(kpis.savingsThisMonth, currency)}
                tone="success"
              />
            </>
          ) : (
            <p className="py-5 text-sm leading-6 text-slate-500">
              Registra movimientos para ver el resumen de tu mes.
            </p>
          )}
        </div>
        {kpis && currency && (
          <p className="mt-2 text-xs leading-5 text-slate-500">
            Esta cantidad es tu límite de gasto menos el gasto variable registrado. No es el saldo
            de tus cuentas ni el estimado de cierre.
          </p>
        )}
      </section>
      <section className="mt-6">
        <h2 className="text-xs font-bold uppercase tracking-wider text-slate-500">
          Consultas de Plus
        </h2>
        <div className="mt-3">
          {meter ? (
            <UsageMeter
              meter={meter}
              accessSource={accessSource}
              accessExpiresAt={accessExpiresAt}
            />
          ) : (
            <p className="rounded-2xl border border-slate-200 p-4 text-sm leading-6 text-slate-500">
              {entitlementLoadFailed || assistantAccessEnabled ? (
                'No pudimos cargar tu disponibilidad.'
              ) : expiredTrialAt ? (
                <>
                  Tu prueba terminó el <ShortDate value={expiredTrialAt} />. No tienes consultas
                  disponibles.
                </>
              ) : (
                'Tu acceso a Plus no está activo en este momento.'
              )}
            </p>
          )}
        </div>
      </section>
      {showProposals && (
        <>
          <PendingProposals
            proposals={proposals}
            loading={proposalsLoading}
            error={proposalsError}
            onRetry={onRetryProposals}
            activeProposalId={activeProposalId}
            activeProposalAction={activeProposalAction}
            failedProposalId={failedProposalId}
            proposalActionError={proposalActionError}
            confirmableActions={confirmableActions}
            onConfirm={onConfirmProposal}
            onCancel={onCancelProposal}
            compact
            className="mt-6"
          />
          {recentProposal && (
            <RecentProposalResult
              proposal={recentProposal}
              interaction={{
                activeProposalId,
                activeProposalAction,
                failedProposalId,
                proposalActionError,
                confirmableActions,
                onConfirm: onConfirmProposal,
                onCancel: onCancelProposal,
              }}
            />
          )}
        </>
      )}
    </aside>
  );
}

function SummaryRow({
  label,
  value,
  tone = 'default',
}: {
  label: string;
  value: string;
  tone?: 'default' | 'warning' | 'expense' | 'success';
}) {
  const colors = {
    default: 'text-slate-900',
    warning: 'text-amber-700',
    expense: 'text-red-600',
    success: 'text-green-700',
  };
  return (
    <div className="flex items-baseline justify-between gap-3 py-3">
      <span className="text-sm text-slate-600">{label}</span>
      <strong className={`text-sm tabular-nums ${colors[tone]}`}>{value}</strong>
    </div>
  );
}

const PROPOSAL_STATUS: Record<
  AiActionProposalStatus,
  {
    title: string;
    label: string;
    border: string;
    header: string;
    badge: string;
  }
> = {
  pending: {
    title: 'Propuesta por aprobar',
    label: 'Sin aplicar',
    border: 'border-slate-300',
    header: 'bg-slate-50',
    badge: 'bg-slate-100 text-slate-600',
  },
  applied: {
    title: 'Movimiento aplicado',
    label: 'Aplicada',
    border: 'border-green-200',
    header: 'bg-green-50',
    badge: 'bg-green-100 text-green-800',
  },
  cancelled: {
    title: 'Propuesta cancelada',
    label: 'Cancelada',
    border: 'border-slate-200',
    header: 'bg-slate-50',
    badge: 'bg-slate-200 text-slate-700',
  },
  expired: {
    title: 'La propuesta venció',
    label: 'Vencida',
    border: 'border-red-200',
    header: 'bg-red-50',
    badge: 'bg-red-100 text-red-800',
  },
  failed: {
    title: 'No se pudo aplicar',
    label: 'Fallida',
    border: 'border-red-200',
    header: 'bg-red-50',
    badge: 'bg-red-100 text-red-800',
  },
};

const CARD_CHARGE_PROPOSAL_STATUS: typeof PROPOSAL_STATUS = {
  pending: {
    title: 'Compra anterior lista para guardar',
    label: 'Aún no guardada',
    border: 'border-slate-300',
    header: 'bg-slate-50',
    badge: 'bg-slate-100 text-slate-600',
  },
  applied: {
    title: 'Compra guardada',
    label: 'Guardada',
    border: 'border-green-200',
    header: 'bg-green-50',
    badge: 'bg-green-100 text-green-800',
  },
  cancelled: {
    title: 'Compra no guardada',
    label: 'No guardada',
    border: 'border-slate-200',
    header: 'bg-slate-50',
    badge: 'bg-slate-200 text-slate-700',
  },
  expired: {
    title: 'La revisión venció',
    label: 'Vencida',
    border: 'border-red-200',
    header: 'bg-red-50',
    badge: 'bg-red-100 text-red-800',
  },
  failed: {
    title: 'No se pudo guardar',
    label: 'No guardada',
    border: 'border-red-200',
    header: 'bg-red-50',
    badge: 'bg-red-100 text-red-800',
  },
};

const CARD_CONFIGURATION_PROPOSAL_STATUS: typeof PROPOSAL_STATUS = {
  pending: {
    title: 'Tarjeta lista para guardar',
    label: 'Aún no guardada',
    border: 'border-slate-300',
    header: 'bg-slate-50',
    badge: 'bg-slate-100 text-slate-600',
  },
  applied: {
    title: 'Tarjeta guardada',
    label: 'Guardada',
    border: 'border-green-200',
    header: 'bg-green-50',
    badge: 'bg-green-100 text-green-800',
  },
  cancelled: {
    title: 'Tarjeta no guardada',
    label: 'No guardada',
    border: 'border-slate-200',
    header: 'bg-slate-50',
    badge: 'bg-slate-200 text-slate-700',
  },
  expired: {
    title: 'La revisión venció',
    label: 'Vencida',
    border: 'border-red-200',
    header: 'bg-red-50',
    badge: 'bg-red-100 text-red-800',
  },
  failed: {
    title: 'No se pudo guardar',
    label: 'No guardada',
    border: 'border-red-200',
    header: 'bg-red-50',
    badge: 'bg-red-100 text-red-800',
  },
};

const CARD_PAYMENT_PROPOSAL_STATUS: typeof PROPOSAL_STATUS = {
  pending: {
    title: 'Pago listo para registrar',
    label: 'Aún no registrado',
    border: 'border-slate-300',
    header: 'bg-slate-50',
    badge: 'bg-slate-100 text-slate-600',
  },
  applied: {
    title: 'Pago registrado',
    label: 'Registrado',
    border: 'border-green-200',
    header: 'bg-green-50',
    badge: 'bg-green-100 text-green-800',
  },
  cancelled: {
    title: 'Pago no registrado',
    label: 'No registrado',
    border: 'border-slate-200',
    header: 'bg-slate-50',
    badge: 'bg-slate-200 text-slate-700',
  },
  expired: {
    title: 'La revisión venció',
    label: 'Vencida',
    border: 'border-red-200',
    header: 'bg-red-50',
    badge: 'bg-red-100 text-red-800',
  },
  failed: {
    title: 'No se pudo registrar',
    label: 'No registrado',
    border: 'border-red-200',
    header: 'bg-red-50',
    badge: 'bg-red-100 text-red-800',
  },
};

function PendingProposals({
  proposals,
  loading,
  error,
  onRetry,
  compact = false,
  hideWhenEmpty = false,
  title = 'Propuestas por aprobar',
  className = '',
  ...interaction
}: {
  proposals: AiActionProposal[];
  loading: boolean;
  error?: string;
  onRetry: () => void;
  compact?: boolean;
  hideWhenEmpty?: boolean;
  title?: string;
  className?: string;
} & ProposalInteractionProps) {
  const headingId = useId();
  if (hideWhenEmpty && !loading && !error && proposals.length === 0) return null;
  return (
    <section className={className} aria-labelledby={headingId}>
      <h2 id={headingId} className="text-xs font-bold uppercase tracking-wider text-slate-500">
        {title}
        {proposals.length > 0 ? ` · ${proposals.length}` : ''}
      </h2>
      {loading && (
        <div role="status" className="mt-3 rounded-2xl border border-slate-200 bg-white p-4">
          <span className="skeleton h-4 w-36" />
          <span className="skeleton mt-3 h-11 w-full" />
          <span className="sr-only">Cargando propuestas pendientes…</span>
        </div>
      )}
      {!loading && error && (
        <div
          role="alert"
          className="mt-3 rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-900"
        >
          <p>{error}</p>
          <button
            type="button"
            onClick={onRetry}
            className="tap-target mt-3 rounded-xl border border-red-200 bg-white px-3 font-semibold text-red-900 hover:bg-red-50"
          >
            Volver a intentar
          </button>
        </div>
      )}
      {!loading && !error && proposals.length === 0 && (
        <div className="mt-3 rounded-2xl border border-dashed border-slate-300 px-4 py-6 text-center">
          <span className="mx-auto inline-flex h-9 w-9 items-center justify-center rounded-xl bg-slate-100 text-slate-400">
            <CheckIcon />
          </span>
          <p className="mt-3 text-sm leading-6 text-slate-500">
            No hay nada pendiente. Cuando el asistente proponga registrar algo, aparecerá aquí para
            que lo revises.
          </p>
        </div>
      )}
      {!loading && !error && proposals.length > 0 && (
        <div className="mt-3 space-y-3">
          {proposals.map((proposal) => (
            <ProposalCard
              key={proposal.id}
              proposal={proposal}
              compact={compact}
              variant="pending-list"
              {...interaction}
            />
          ))}
        </div>
      )}
    </section>
  );
}

function RecentProposalResult({
  proposal,
  interaction,
}: {
  proposal: AiActionProposal;
  interaction: ProposalInteractionProps;
}) {
  const headingId = useId();
  return (
    <section className="mt-6" aria-labelledby={headingId}>
      <h2 id={headingId} className="text-xs font-bold uppercase tracking-wider text-slate-500">
        Resultado reciente
      </h2>
      <div className="mt-3">
        <ProposalCard proposal={proposal} compact variant="pending-list" {...interaction} />
      </div>
    </section>
  );
}

function ProposalCard({
  proposal,
  compact = false,
  variant = 'inline',
  ...interaction
}: {
  proposal: AiActionProposal;
  compact?: boolean;
  variant?: 'inline' | 'pending-list';
} & ProposalInteractionProps) {
  const runtimeAction = (proposal as { actionType?: unknown }).actionType;
  if (!isKnownProposalAction(runtimeAction)) {
    return <UnsupportedProposalCard proposal={proposal} variant={variant} />;
  }
  return (
    <KnownProposalCard proposal={proposal} compact={compact} variant={variant} {...interaction} />
  );
}

function KnownProposalCard({
  proposal,
  compact,
  variant,
  activeProposalId,
  activeProposalAction,
  failedProposalId,
  proposalActionError,
  confirmableActions,
  onConfirm,
  onCancel,
}: {
  proposal: AiActionProposal;
  compact: boolean;
  variant: 'inline' | 'pending-list';
} & ProposalInteractionProps) {
  const [expanded, setExpanded] = useState(false);
  const [observedNow, setObservedNow] = useState(() => Date.now());
  const detailsId = useId();
  const fieldsId = useId();
  const expiresAt = new Date(proposal.expiresAt).getTime();
  const displayStatus: AiActionProposalStatus =
    proposal.status === 'pending' && expiresAt <= observedNow ? 'expired' : proposal.status;
  const presentation = getProposalPresentation(proposal, displayStatus);
  const { appearance } = presentation;
  const busy = activeProposalId === proposal.id;
  const anotherProposalBusy = Boolean(activeProposalId && !busy);
  const showFields = !compact || expanded;
  const actionError = failedProposalId === proposal.id ? proposalActionError : undefined;
  const confirmationEnabled = confirmableActions.includes(proposal.actionType);
  const controlsClass = compact ? 'grid grid-cols-2 gap-2' : 'grid grid-cols-2 gap-2 sm:flex';
  const Heading = variant === 'inline' ? 'h2' : 'h3';

  useEffect(() => {
    if (proposal.status !== 'pending' || !Number.isFinite(expiresAt)) return;
    const remaining = expiresAt - Date.now();
    if (remaining <= 0) return;
    const timer = window.setTimeout(
      () => setObservedNow(Date.now()),
      Math.min(remaining + 50, 60_000),
    );
    return () => window.clearTimeout(timer);
  }, [expiresAt, observedNow, proposal.status]);

  return (
    <article
      className={`overflow-hidden rounded-2xl border bg-white shadow-sm ${appearance.border}`}
      aria-label={`${appearance.title}: ${presentation.subject}${presentation.amount ? ` por ${presentation.amount}` : ''}`}
      aria-busy={busy}
      data-proposal-id={proposal.id}
      data-proposal-variant={variant}
    >
      <header
        className={`flex items-center justify-between gap-3 border-b border-slate-100 px-3.5 py-2.5 sm:py-3 ${appearance.header}`}
      >
        <Heading className="text-sm font-bold text-slate-900">{appearance.title}</Heading>
        <span
          className={`shrink-0 rounded-md px-2 py-1 text-[10px] font-bold uppercase tracking-wide ${appearance.badge}`}
        >
          {appearance.label}
        </span>
      </header>

      {compact && !expanded && (
        <div className="p-3.5">
          <ProposalCompactSummary proposal={proposal} />
        </div>
      )}

      <div id={fieldsId} hidden={!showFields} className="p-3 sm:p-4">
        <ProposalFields proposal={proposal} />
        <div className="mt-3 rounded-xl border border-slate-200 bg-slate-50 p-2.5 sm:mt-4 sm:p-3">
          <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500">
            {presentation.effectTitle}
          </p>
          {proposal.effects.length > 0 ? (
            <ul className="mt-2 space-y-1.5 text-sm leading-5 text-slate-700">
              {proposal.effects.map((effect) => (
                <li key={effect.label}>
                  <span>{effect.label}: </span>
                  <span className="tabular-nums">
                    {formatMoney(effect.before, effect.currency)} →{' '}
                    <strong>{formatMoney(effect.after, effect.currency)}</strong>
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="mt-2 text-sm leading-5 text-slate-600">{presentation.emptyEffect}</p>
          )}
        </div>
        {presentation.info && (
          <p className="mt-3 rounded-xl bg-blue-50 p-2.5 text-xs leading-5 text-blue-900">
            {presentation.info}
          </p>
        )}
      </div>
      <div
        id={detailsId}
        hidden={!expanded}
        className="mx-3.5 mb-3.5 rounded-xl bg-slate-50 px-3 py-2.5 text-xs leading-5 text-slate-600"
      >
        <p>
          Preparada el{' '}
          <time dateTime={proposal.createdAt}>{formatShortDate(proposal.createdAt)}</time>.
        </p>
        <p>
          {displayStatus === 'pending'
            ? proposalExpiryLabel(proposal.expiresAt)
            : presentation.usesReviewLanguage
              ? `Esta revisión estuvo disponible hasta el ${formatShortDate(proposal.expiresAt)}.`
              : `Vencimiento original: ${formatShortDate(proposal.expiresAt)}.`}
        </p>
      </div>

      {displayStatus === 'applied' && (
        <div
          role="status"
          className="mx-3.5 mb-3.5 rounded-xl border border-green-200 bg-green-50 p-3 text-sm leading-5 text-green-900"
        >
          <strong className="block">{presentation.appliedMessage}</strong>
          {presentation.appliedLink && (
            <Link
              className="tap-target mt-2 inline-flex items-center font-semibold text-green-800 underline"
              to={presentation.appliedLink.to}
            >
              {presentation.appliedLink.label}
            </Link>
          )}
        </div>
      )}
      {displayStatus === 'cancelled' && (
        <p
          role="status"
          className="mx-3.5 mb-3.5 rounded-xl bg-slate-100 p-3 text-sm leading-5 text-slate-700"
        >
          {presentation.cancelledMessage}
        </p>
      )}
      {displayStatus === 'expired' && (
        <div
          role="status"
          className="mx-3.5 mb-3.5 rounded-xl border border-red-200 bg-red-50 p-3 text-sm leading-5 text-red-900"
        >
          <strong className="block">No se registró nada.</strong>
          <p>{presentation.expiredMessage}</p>
          {presentation.expiredLink && (
            <Link
              className="tap-target mt-2 inline-flex font-semibold underline"
              to={presentation.expiredLink.to}
            >
              {presentation.expiredLink.label}
            </Link>
          )}
        </div>
      )}
      {displayStatus === 'failed' && (
        <p
          role="alert"
          className="mx-3.5 mb-3.5 rounded-xl border border-red-200 bg-red-50 p-3 text-sm leading-5 text-red-900"
        >
          {presentation.failedMessage}
        </p>
      )}
      {actionError && (
        <p
          role="alert"
          className="mx-3.5 mb-3.5 rounded-xl border border-red-200 bg-red-50 p-3 text-sm leading-5 text-red-900"
        >
          {actionError}
        </p>
      )}
      {displayStatus === 'pending' && !confirmationEnabled && (
        <p
          role="status"
          className="mx-3.5 mb-3.5 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm leading-5 text-amber-900"
        >
          Esta acción está temporalmente desactivada. No se registró nada. Puedes cancelar esta
          propuesta.
        </p>
      )}

      <footer className={`border-t border-slate-100 bg-[#FCFCFB] p-3 sm:p-3.5 ${controlsClass}`}>
        {displayStatus === 'pending' && (
          <>
            {confirmationEnabled && (
              <button
                type="button"
                onClick={() => onConfirm(proposal.id)}
                disabled={busy || anotherProposalBusy}
                className="tap-target col-span-2 inline-flex items-center justify-center gap-2 rounded-xl bg-slate-900 px-4 text-sm font-semibold text-white hover:bg-slate-800 disabled:cursor-not-allowed disabled:bg-slate-300"
              >
                <CheckIcon />
                {busy && activeProposalAction === 'confirm'
                  ? presentation.confirmingLabel
                  : presentation.confirmLabel}
              </button>
            )}
            <button
              type="button"
              onClick={() => onCancel(proposal.id)}
              disabled={busy || anotherProposalBusy}
              className="tap-target inline-flex items-center justify-center rounded-xl border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:text-slate-400"
            >
              {busy && activeProposalAction === 'cancel' ? 'Cancelando…' : presentation.cancelLabel}
            </button>
          </>
        )}
        <button
          type="button"
          aria-expanded={expanded}
          aria-controls={compact ? `${fieldsId} ${detailsId}` : detailsId}
          onClick={() => setExpanded((value) => !value)}
          className="tap-target inline-flex items-center justify-center rounded-xl px-3 text-sm font-semibold text-blue-700 hover:bg-blue-50"
        >
          {expanded ? 'Ocultar detalle' : 'Ver detalle'}
        </button>
        {displayStatus === 'pending' && (
          <time
            dateTime={proposal.expiresAt}
            className="col-span-2 text-center text-xs text-stone-500 sm:ml-auto sm:self-center"
          >
            {proposalExpiryLabel(proposal.expiresAt)}
          </time>
        )}
      </footer>
    </article>
  );
}

type ProposalAppearance = (typeof PROPOSAL_STATUS)[AiActionProposalStatus];

interface ProposalPresentation {
  appearance: ProposalAppearance;
  subject: string;
  amount?: string;
  effectTitle: string;
  emptyEffect: string;
  info?: string;
  confirmLabel: string;
  confirmingLabel: string;
  cancelLabel: string;
  appliedMessage: string;
  appliedLink?: { to: string; label: string };
  cancelledMessage: string;
  expiredMessage: string;
  expiredLink?: { to: string; label: string };
  failedMessage: string;
  usesReviewLanguage: boolean;
}

function getProposalPresentation(
  proposal: AiActionProposal,
  status: AiActionProposalStatus,
): ProposalPresentation {
  switch (proposal.actionType) {
    case 'create_transaction': {
      const typeLabel = proposal.payload.type === TransactionType.INCOME ? 'Ingreso' : 'Gasto';
      const createsCategory = proposal.payload.category.status === 'will_create';
      return {
        appearance: PROPOSAL_STATUS[status],
        subject: typeLabel,
        amount: `${formatMoney(proposal.payload.amount, proposal.payload.currency)} ${proposal.payload.currency}`,
        effectTitle: 'Efecto previsto',
        emptyEffect: 'El servidor volverá a validar el movimiento antes de registrarlo.',
        confirmLabel: createsCategory
          ? 'Crear categoría y guardar movimiento'
          : 'Guardar movimiento',
        confirmingLabel: 'Guardando…',
        cancelLabel: 'Cancelar',
        appliedMessage: createsCategory
          ? `El movimiento se guardó. También creamos la categoría ${proposalCategoryName(proposal.payload.category)}.`
          : 'El movimiento se registró correctamente.',
        ...(proposal.createdRecordId
          ? {
              appliedLink: {
                to: `/transacciones?highlight=${encodeURIComponent(proposal.createdRecordId)}`,
                label: 'Ver movimiento',
              },
            }
          : {}),
        cancelledMessage:
          'La cancelaste. No se registró ningún movimiento y no se hizo otra consulta al asistente.',
        expiredMessage: 'La propuesta venció y no se volverá a intentar por sí sola.',
        expiredLink: { to: '/transacciones', label: 'Registrarlo manualmente' },
        failedMessage:
          'Los datos cambiaron o dejaron de ser válidos. No se registró ningún movimiento.',
        usesReviewLanguage: false,
      };
    }
    case 'create_card_charge': {
      const { payload } = proposal;
      const isNewPurchase = payload.captureMode === 'new';
      const createsCategory = payload.category.status === 'will_create';
      const appearance = CARD_CHARGE_PROPOSAL_STATUS[status];
      return {
        appearance:
          isNewPurchase && status === 'pending'
            ? { ...appearance, title: 'Compra lista para guardar' }
            : appearance,
        subject: payload.note ?? (isNewPurchase ? 'Compra con tarjeta' : 'Compra anterior'),
        amount: `${formatMoney(payload.totalAmount, payload.currency)} ${payload.currency}`,
        effectTitle: 'Así cambiaría tu tarjeta',
        emptyEffect: 'La app revisará de nuevo la deuda y el crédito disponible antes de guardar.',
        info: 'Guardar esto no saca dinero de tus cuentas. Cuando pagues la tarjeta, registra ese pago por separado.',
        confirmLabel: createsCategory ? 'Crear categoría y guardar compra' : 'Guardar compra',
        confirmingLabel: 'Guardando…',
        cancelLabel: 'No guardar',
        appliedMessage: createsCategory
          ? `La compra se guardó en ${payload.cardName}. También creamos la categoría ${proposalCategoryName(payload.category)}.`
          : `La compra se guardó en ${payload.cardName}.`,
        appliedLink: { to: '/tarjeta', label: 'Ver en Tarjeta' },
        cancelledMessage:
          'No guardamos la compra ni cambiamos tu deuda. No se usó otra consulta al asistente.',
        expiredMessage: 'La revisión venció. Tus datos siguen sin cambios.',
        expiredLink: { to: '/tarjeta', label: 'Abrir Tarjeta' },
        failedMessage:
          'Los datos de la tarjeta cambiaron. No guardamos la compra ni cambiamos tu deuda.',
        usesReviewLanguage: true,
      };
    }
    case 'configure_credit_card':
      return {
        appearance: CARD_CONFIGURATION_PROPOSAL_STATUS[status],
        subject: proposal.payload.cardName,
        effectTitle: 'Así quedará tu tarjeta',
        emptyEffect: 'La app revisará estos datos de nuevo antes de guardar la tarjeta.',
        info: 'Guardar esto no se conecta a tu banco ni crea compras o pagos. La app usará estos datos para ayudarte a calcular lo que debes y tus próximas fechas.',
        confirmLabel: 'Guardar tarjeta',
        confirmingLabel: 'Guardando…',
        cancelLabel: 'No guardar',
        appliedMessage: `${proposal.payload.cardName} quedó lista.`,
        appliedLink: { to: '/tarjeta', label: 'Ver en Tarjeta' },
        cancelledMessage:
          'No guardamos la tarjeta. Tus datos siguen sin cambios y no se usó otra consulta.',
        expiredMessage: 'La revisión venció. La tarjeta no se guardó.',
        expiredLink: { to: '/tarjeta', label: 'Abrir Tarjeta' },
        failedMessage:
          'No pudimos guardar la tarjeta porque los datos cambiaron o dejaron de ser válidos. No se hizo ningún cambio.',
        usesReviewLanguage: true,
      };
    case 'create_card_payment':
      return {
        appearance: CARD_PAYMENT_PROPOSAL_STATUS[status],
        subject: `Pago a ${proposal.payload.cardName}`,
        amount: `${formatMoney(proposal.payload.amount, proposal.payload.currency)} ${proposal.payload.currency}`,
        effectTitle: 'Así cambiarán tus saldos',
        emptyEffect:
          'La app revisará de nuevo la deuda y el dinero disponible antes de registrar el pago.',
        info: `Al registrarlo, la app restará este monto del saldo anotado en ${proposal.payload.sourceAccountName} y bajará la deuda registrada de la tarjeta. No mueve dinero en el banco. No se contará como gasto otra vez. Las dos cifras de la cuenta muestran el saldo total y cuánto queda sin apartar; el descuento se hace una sola vez.`,
        confirmLabel: 'Registrar pago',
        confirmingLabel: 'Registrando…',
        cancelLabel: 'No registrar',
        appliedMessage: `El pago a ${proposal.payload.cardName} se registró.`,
        appliedLink: { to: '/tarjeta', label: 'Ver en Tarjeta' },
        cancelledMessage:
          'No registramos el pago. La deuda y el saldo anotado de tu cuenta siguen igual. No se usó otra consulta.',
        expiredMessage: 'La revisión venció. No cambiamos el saldo anotado ni la deuda.',
        expiredLink: { to: '/tarjeta', label: 'Abrir Tarjeta' },
        failedMessage:
          'La deuda o el dinero disponible cambiaron. No registramos el pago ni cambiamos el saldo anotado de tu cuenta.',
        usesReviewLanguage: true,
      };
    case 'create_account':
      return {
        appearance: PROPOSAL_STATUS[status],
        subject: proposal.payload.accountName,
        amount: `${formatMoney(proposal.payload.openingBalance, proposal.payload.currency)} ${proposal.payload.currency}`,
        effectTitle: 'Así quedará la cuenta',
        emptyEffect:
          'La app volverá a comprobar que no exista una cuenta equivalente antes de crearla.',
        info: 'El saldo inicial representa el dinero que ya tienes; crear la cuenta no mueve dinero ni registra un ingreso.',
        confirmLabel: 'Guardar cuenta',
        confirmingLabel: 'Guardando…',
        cancelLabel: 'No guardar',
        appliedMessage: `${proposal.payload.accountName} quedó lista.`,
        appliedLink: { to: '/cuentas', label: 'Ver en Cuentas' },
        cancelledMessage:
          'No guardamos la cuenta. Tus datos siguen sin cambios y no se usó otra consulta.',
        expiredMessage: 'La revisión venció. La cuenta no se creó.',
        expiredLink: { to: '/cuentas', label: 'Abrir Cuentas' },
        failedMessage:
          'La cuenta ya existe o los datos dejaron de ser válidos. No se creó ninguna cuenta.',
        usesReviewLanguage: true,
      };
    default:
      return assertNever(proposal);
  }
}

function ProposalCompactSummary({ proposal }: { proposal: AiActionProposal }) {
  switch (proposal.actionType) {
    case 'create_transaction': {
      const typeLabel = proposal.payload.type === TransactionType.INCOME ? 'Ingreso' : 'Gasto';
      return (
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-sm font-semibold text-slate-900">{typeLabel}</p>
            <p className="mt-1 truncate text-xs text-slate-500">
              {proposalCategoryName(proposal.payload.category)} ·{' '}
              {formatShortDate(proposal.payload.date)}
            </p>
            {proposal.payload.category.status === 'will_create' && (
              <p className="mt-1 text-[11px] font-semibold text-blue-700">
                La categoría se creará al guardar
              </p>
            )}
          </div>
          <strong className="shrink-0 text-sm tabular-nums text-slate-900">
            {formatMoney(proposal.payload.amount, proposal.payload.currency)}
          </strong>
        </div>
      );
    }
    case 'create_card_charge': {
      const { payload } = proposal;
      const isNewPurchase = payload.captureMode === 'new';
      return (
        <div>
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold text-slate-900">
                {payload.note ?? (isNewPurchase ? 'Compra con tarjeta' : 'Compra anterior')}
              </p>
              <p className="mt-1 truncate text-xs text-slate-500">
                {payload.cardName} ·{' '}
                {isNewPurchase
                  ? cardChargePlanLabel(payload)
                  : `${payload.msiPaid} de ${payload.msiMonths} pagos cubiertos`}
              </p>
            </div>
            <strong className="shrink-0 text-sm tabular-nums text-slate-900">
              {formatMoney(payload.totalAmount, payload.currency)}
            </strong>
          </div>
          <p className="mt-2 text-xs leading-5 text-slate-600">
            {isNewPurchase ? (
              <>
                Deuda registrada: +{formatMoney(payload.balanceImpactAmount, payload.currency)}
                {payload.msiMonths > 1
                  ? ` · ${formatMoney(payload.monthlyAmount, payload.currency)} al mes`
                  : ''}
              </>
            ) : (
              <>
                {formatMoney(payload.monthlyAmount, payload.currency)} al mes · faltan{' '}
                {payload.monthsRemaining} {payload.monthsRemaining === 1 ? 'pago' : 'pagos'}
              </>
            )}
          </p>
          <p className="mt-1 text-xs leading-5 text-slate-600">
            Categoría: {proposalCategoryName(payload.category)}
            {payload.category.status === 'will_create' ? ' · se creará al guardar' : ''}
          </p>
        </div>
      );
    }
    case 'configure_credit_card':
      return (
        <div>
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold text-slate-900">
                {proposal.payload.cardName}
              </p>
              <p className="mt-1 truncate text-xs text-slate-500">
                {proposal.payload.institution ?? 'Banco o institución sin indicar'}
              </p>
            </div>
            <strong className="shrink-0 text-sm tabular-nums text-slate-900">
              {formatMoney(proposal.payload.creditLimit, proposal.payload.currency)}
            </strong>
          </div>
          <p className="mt-2 text-xs leading-5 text-slate-600">
            Debes hoy {formatMoney(proposal.payload.currentDebt, proposal.payload.currency)} ·
            disponible {formatMoney(proposal.payload.availableCredit, proposal.payload.currency)}
          </p>
        </div>
      );
    case 'create_card_payment':
      return (
        <div>
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold text-slate-900">
                Pago a {proposal.payload.cardName}
              </p>
              <p className="mt-1 truncate text-xs text-slate-500">
                Desde {proposal.payload.sourceAccountName} ·{' '}
                {formatShortDate(proposal.payload.date)}
              </p>
            </div>
            <strong className="shrink-0 text-sm tabular-nums text-slate-900">
              {formatMoney(proposal.payload.amount, proposal.payload.currency)}
            </strong>
          </div>
          <p className="mt-2 text-xs leading-5 text-slate-600">
            Después deberás {formatMoney(proposal.payload.cardDebtAfter, proposal.payload.currency)}
          </p>
        </div>
      );
    case 'create_account':
      return (
        <div>
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold text-slate-900">
                {proposal.payload.accountName}
              </p>
              <p className="mt-1 truncate text-xs text-slate-500">
                {accountTypeLabel(proposal.payload.accountType)} · {proposal.payload.currency}
              </p>
            </div>
            <strong className="shrink-0 text-sm tabular-nums text-slate-900">
              {formatMoney(proposal.payload.openingBalance, proposal.payload.currency)}
            </strong>
          </div>
          <p className="mt-2 text-xs leading-5 text-slate-600">Saldo inicial registrado</p>
        </div>
      );
    default:
      return assertNever(proposal);
  }
}

function ProposalFields({ proposal }: { proposal: AiActionProposal }) {
  switch (proposal.actionType) {
    case 'create_transaction': {
      const typeLabel = proposal.payload.type === TransactionType.INCOME ? 'Ingreso' : 'Gasto';
      return (
        <dl
          data-testid="assistant-proposal-fields"
          className="grid grid-cols-1 gap-x-3 gap-y-2.5 min-[360px]:grid-cols-2 sm:gap-x-5 sm:gap-y-3"
        >
          <ProposalField label="Tipo" value={typeLabel} />
          <ProposalField
            label="Importe"
            value={`${formatMoney(proposal.payload.amount, proposal.payload.currency)} ${proposal.payload.currency}`}
            strong
          />
          <ProposalField label="Fecha" value={formatShortDate(proposal.payload.date)} />
          <ProposalField label="Cuenta" value={proposal.payload.accountName} />
          <ProposalCategoryField category={proposal.payload.category} />
          <ProposalField
            className="min-[360px]:col-span-2"
            label="Descripción"
            value={proposal.payload.description ?? 'Sin descripción'}
          />
        </dl>
      );
    }
    case 'create_card_charge':
      return <CardChargeProposalFields proposal={proposal} />;
    case 'configure_credit_card':
      return <CreditCardConfigurationProposalFields proposal={proposal} />;
    case 'create_card_payment':
      return <CardPaymentProposalFields proposal={proposal} />;
    case 'create_account':
      return (
        <dl
          data-testid="assistant-proposal-fields"
          className="grid grid-cols-1 gap-x-3 gap-y-2.5 min-[360px]:grid-cols-2 sm:gap-x-5 sm:gap-y-3"
        >
          <ProposalField label="Nombre" value={proposal.payload.accountName} />
          <ProposalField label="Tipo" value={accountTypeLabel(proposal.payload.accountType)} />
          <ProposalField label="Moneda" value={proposal.payload.currency} />
          <ProposalField
            label="Saldo inicial"
            value={`${formatMoney(proposal.payload.openingBalance, proposal.payload.currency)} ${proposal.payload.currency}`}
            strong
          />
          <ProposalField
            label="Institución"
            value={proposal.payload.institution ?? 'Sin institución'}
          />
          <ProposalField
            label="Patrimonio"
            value={proposal.payload.includeInNetWorth ? 'Sí se incluye' : 'No se incluye'}
          />
        </dl>
      );
    default:
      return assertNever(proposal);
  }
}

function isKnownProposalAction(value: unknown): value is AiActionProposalType {
  return (
    value === 'create_transaction' ||
    value === 'create_card_charge' ||
    value === 'configure_credit_card' ||
    value === 'create_card_payment' ||
    value === 'create_account'
  );
}

function accountTypeLabel(value: string): string {
  const labels: Record<string, string> = {
    bank: 'Bancaria',
    savings: 'Ahorro',
    cetes: 'CETES',
    caja_ahorro: 'Caja de ahorro',
    cash: 'Efectivo',
  };
  return labels[value] ?? value;
}

function UnsupportedProposalCard({
  proposal,
  variant,
}: {
  proposal: AiActionProposal;
  variant: 'inline' | 'pending-list';
}) {
  const id =
    typeof (proposal as { id?: unknown }).id === 'string'
      ? (proposal as { id: string }).id
      : 'unknown';
  return (
    <article
      role="alert"
      data-proposal-id={id}
      data-proposal-variant={variant}
      className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm leading-6 text-amber-950"
    >
      <h2 className="font-bold">Actualiza la app para revisar esta propuesta</h2>
      <p className="mt-1">
        No mostramos botones para evitar que confirmes algo que esta versión todavía no entiende.
      </p>
      <button
        type="button"
        onClick={() => window.location.reload()}
        className="tap-target mt-3 rounded-xl bg-slate-900 px-4 text-sm font-semibold text-white"
      >
        Recargar la app
      </button>
    </article>
  );
}

function assertNever(value: never): never {
  throw new Error(`Tipo de propuesta no soportado: ${String(value)}`);
}

type CardChargeProposal = Extract<AiActionProposal, { actionType: 'create_card_charge' }>;
type CardChargePayload = CardChargeProposal['payload'];
type NewCardChargePayload = Extract<CardChargePayload, { captureMode: 'new' }>;
type HistoricalCardChargePayload = Extract<CardChargePayload, { captureMode: 'historical' }>;

function cardChargePlanLabel(payload: CardChargePayload): string {
  return payload.msiMonths === 1 ? 'Un solo pago' : `${payload.msiMonths} meses sin intereses`;
}

function CardChargeProposalFields({ proposal }: { proposal: CardChargeProposal }) {
  const { payload } = proposal;

  return payload.captureMode === 'new' ? (
    <NewCardChargeProposalFields payload={payload} />
  ) : (
    <HistoricalCardChargeProposalFields payload={payload} />
  );
}

function NewCardChargeProposalFields({ payload }: { payload: NewCardChargePayload }) {
  const cardLabel = payload.institution
    ? `${payload.cardName} · ${payload.institution}`
    : payload.cardName;
  const isMsi = payload.msiMonths > 1;
  const balanceExplanation = isMsi
    ? `Al guardar, se sumará el precio completo de ${formatMoney(payload.balanceImpactAmount, payload.currency)} a la deuda. Después pagarás ${formatMoney(payload.monthlyAmount, payload.currency)} al mes durante ${payload.msiMonths} meses.`
    : `Al guardar, se sumarán ${formatMoney(payload.balanceImpactAmount, payload.currency)} a la deuda registrada.`;

  return (
    <>
      <dl
        data-testid="assistant-proposal-fields"
        className="grid grid-cols-1 gap-x-3 gap-y-2.5 min-[360px]:grid-cols-2 sm:gap-x-5 sm:gap-y-3"
      >
        <ProposalField
          className="min-[360px]:col-span-2"
          label="Compra"
          value={payload.note ?? 'Sin nombre'}
        />
        <ProposalField
          label="Precio"
          value={`${formatMoney(payload.totalAmount, payload.currency)} ${payload.currency}`}
          strong
        />
        <ProposalField label="Plan" value={cardChargePlanLabel(payload)} strong />
        {isMsi && (
          <ProposalField
            label="Pago al mes"
            value={formatMoney(payload.monthlyAmount, payload.currency)}
            strong
          />
        )}
        <ProposalField label="Fecha de compra" value={formatShortDate(payload.purchaseDate)} />
        <ProposalField className="min-[360px]:col-span-2" label="Tarjeta" value={cardLabel} />
        <ProposalCategoryField className="min-[360px]:col-span-2" category={payload.category} />
      </dl>
      <div className="mt-3 rounded-xl border border-blue-100 bg-blue-50 p-2.5 text-sm leading-5 text-blue-950">
        <strong className="block">Cómo cambiará tu deuda</strong>
        <span>{balanceExplanation}</span>
      </div>
    </>
  );
}

function HistoricalCardChargeProposalFields({ payload }: { payload: HistoricalCardChargePayload }) {
  const cardLabel = payload.institution
    ? `${payload.cardName} · ${payload.institution}`
    : payload.cardName;
  const nextInstallment = payload.monthsRemaining > 0 ? payload.msiPaid + 1 : null;
  const balanceExplanation =
    payload.balanceTreatment === 'alreadyIncluded'
      ? 'Lo que falta ya está incluido en la deuda registrada. Guardar la compra no cambiará ese saldo.'
      : `Se sumarán ${formatMoney(payload.balanceImpactAmount, payload.currency)} a la deuda registrada.`;

  return (
    <>
      <dl
        data-testid="assistant-proposal-fields"
        className="grid grid-cols-1 gap-x-3 gap-y-2.5 min-[360px]:grid-cols-2 sm:gap-x-5 sm:gap-y-3"
      >
        <ProposalField
          className="min-[360px]:col-span-2"
          label="Compra"
          value={payload.note ?? 'Sin nombre'}
        />
        <ProposalField
          label="Precio original"
          value={`${formatMoney(payload.totalAmount, payload.currency)} ${payload.currency}`}
          strong
        />
        <ProposalField
          label="Pago mensual"
          value={formatMoney(payload.monthlyAmount, payload.currency)}
          strong
        />
        <ProposalField label="Ya pagaste" value={`${payload.msiPaid} de ${payload.msiMonths}`} />
        <ProposalField
          label="Faltan"
          value={`${payload.monthsRemaining} ${payload.monthsRemaining === 1 ? 'pago' : 'pagos'}`}
        />
        <ProposalField
          label="Falta por pagar"
          value={formatMoney(payload.remainingBalance, payload.currency)}
          strong
        />
        <ProposalField
          label="Pago que sigue"
          value={nextInstallment ? `${nextInstallment} de ${payload.msiMonths}` : 'Ninguno'}
        />
        <ProposalField className="min-[360px]:col-span-2" label="Tarjeta" value={cardLabel} />
        <ProposalField label="Fecha de compra" value={formatShortDate(payload.purchaseDate)} />
        <ProposalField label="Plan" value={`${payload.msiMonths} meses sin intereses`} />
        <ProposalCategoryField className="min-[360px]:col-span-2" category={payload.category} />
      </dl>
      <div className="mt-3 rounded-xl border border-blue-100 bg-blue-50 p-2.5 text-sm leading-5 text-blue-950">
        <strong className="block">Cómo se tomará en cuenta la deuda</strong>
        <span>{balanceExplanation}</span>
      </div>
    </>
  );
}

type CreditCardConfigurationProposal = Extract<
  AiActionProposal,
  { actionType: 'configure_credit_card' }
>;

function CreditCardConfigurationProposalFields({
  proposal,
}: {
  proposal: CreditCardConfigurationProposal;
}) {
  const { payload } = proposal;
  return (
    <dl
      data-testid="assistant-proposal-fields"
      className="grid grid-cols-1 gap-x-3 gap-y-2.5 min-[360px]:grid-cols-2 sm:gap-x-5 sm:gap-y-3"
    >
      <ProposalField
        className="min-[360px]:col-span-2"
        label="Nombre de la tarjeta"
        value={payload.cardName}
      />
      <ProposalField
        className="min-[360px]:col-span-2"
        label="Banco o institución"
        value={payload.institution ?? 'Sin indicar'}
      />
      <ProposalField
        label="Cuánto debes hoy"
        value={`${formatMoney(payload.currentDebt, payload.currency)} ${payload.currency}`}
        strong
      />
      <ProposalField
        label="Límite de crédito"
        value={formatMoney(payload.creditLimit, payload.currency)}
        strong
      />
      <ProposalField
        label="Crédito disponible"
        value={formatMoney(payload.availableCredit, payload.currency)}
        strong
      />
      <ProposalField label="Moneda" value={payload.currency} />
      <ProposalField label="Día de corte" value={`Día ${payload.cutoffDay}`} />
      <ProposalField label="Tu pago vence" value={`Día ${payload.paymentDay}`} />
    </dl>
  );
}

type CardPaymentProposal = Extract<AiActionProposal, { actionType: 'create_card_payment' }>;

function CardPaymentProposalFields({ proposal }: { proposal: CardPaymentProposal }) {
  const { payload } = proposal;
  const cardLabel = payload.institution
    ? `${payload.cardName} · ${payload.institution}`
    : payload.cardName;
  return (
    <dl
      data-testid="assistant-proposal-fields"
      className="grid grid-cols-1 gap-x-3 gap-y-2.5 min-[360px]:grid-cols-2 sm:gap-x-5 sm:gap-y-3"
    >
      <ProposalField
        label="Pago"
        value={`${formatMoney(payload.amount, payload.currency)} ${payload.currency}`}
        strong
      />
      <ProposalField label="Fecha" value={formatShortDate(payload.date)} />
      <ProposalField
        className="min-[360px]:col-span-2"
        label="Sale de"
        value={payload.sourceAccountName}
      />
      <ProposalField className="min-[360px]:col-span-2" label="Tarjeta" value={cardLabel} />
      <ProposalField
        label="Deuda después"
        value={formatMoney(payload.cardDebtAfter, payload.currency)}
        strong
      />
      <ProposalField
        label="Dinero libre después"
        value={formatMoney(payload.accountAvailableAfter, payload.currency)}
        strong
      />
      <ProposalField
        className="min-[360px]:col-span-2"
        label="Nota"
        value={payload.note ?? 'Sin nota'}
      />
    </dl>
  );
}

type ProposalCategory = Extract<
  AiActionProposal,
  { actionType: 'create_transaction' }
>['payload']['category'];

function proposalCategoryName(category: ProposalCategory): string {
  return category.name ?? 'Sin categoría';
}

function ProposalCategoryField({
  category,
  className = '',
}: {
  category: ProposalCategory;
  className?: string;
}) {
  const willCreate = category.status === 'will_create';
  return (
    <div className={className}>
      <dt className="text-xs text-slate-500">{willCreate ? 'Categoría sugerida' : 'Categoría'}</dt>
      <dd className="mt-0.5 text-sm font-semibold text-slate-900">
        {proposalCategoryName(category)}
      </dd>
      {willCreate && (
        <span className="mt-1 inline-flex rounded-md bg-blue-50 px-2 py-1 text-[11px] font-semibold text-blue-700">
          Nueva · se creará al guardar
        </span>
      )}
    </div>
  );
}

function ProposalField({
  label,
  value,
  strong = false,
  className = '',
}: {
  label: string;
  value: string;
  strong?: boolean;
  className?: string;
}) {
  return (
    <div className={className}>
      <dt className="text-xs text-slate-500">{label}</dt>
      <dd
        className={`mt-0.5 text-sm text-slate-900 ${strong ? 'font-bold tabular-nums' : 'font-semibold'}`}
      >
        {value}
      </dd>
    </div>
  );
}

function proposalExpiryLabel(value: string): string {
  const milliseconds = new Date(value).getTime() - Date.now();
  if (milliseconds <= 0) return `Venció el ${formatShortDate(value)}`;
  const minutes = Math.ceil(milliseconds / 60_000);
  if (minutes > 0 && minutes <= 60) return `Vence en ${minutes} min`;
  return `Vence el ${formatShortDate(value)}`;
}

function UsageIcon() {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.9"
      className="h-4 w-4"
    >
      <circle cx="12" cy="12" r="8" />
      <path d="M12 7v5l3 2" />
    </svg>
  );
}
function PlusIcon() {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      className="h-5 w-5"
    >
      <path d="M12 5v14M5 12h14" />
    </svg>
  );
}
function MoreIcon() {
  return (
    <svg aria-hidden="true" viewBox="0 0 24 24" fill="currentColor" className="h-5 w-5">
      <circle cx="5" cy="12" r="1.5" />
      <circle cx="12" cy="12" r="1.5" />
      <circle cx="19" cy="12" r="1.5" />
    </svg>
  );
}
function SendIcon() {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.9"
      className="h-5 w-5"
    >
      <path d="m4 4 16 8-16 8 3-8-3-8Z" />
      <path d="M7 12h13" />
    </svg>
  );
}
function CheckIcon() {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.9"
      className="h-5 w-5"
    >
      <path d="m5 12 4.2 4.2L19 6.5" />
    </svg>
  );
}

function Welcome({
  period,
  onPick,
  canUseAnalysis,
  confirmableActions,
}: {
  period: string | undefined;
  onPick: (value: (typeof SUGGESTIONS)[number]) => void;
  canUseAnalysis: boolean;
  confirmableActions: readonly AiActionProposalType[];
}) {
  const [showMoreSuggestions, setShowMoreSuggestions] = useState(false);
  const extraSuggestionsId = useId();
  const availableSuggestions = SUGGESTIONS.filter(
    (item) =>
      item.kind !== 'action' ||
      ('requiredAction' in item && confirmableActions.includes(item.requiredAction)),
  );
  return (
    <div className="max-w-2xl lg:my-auto">
      <span
        className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-slate-900 text-xl text-emerald-300"
        aria-hidden
      >
        ✦
      </span>
      <h2 className="mt-4 text-2xl font-bold tracking-tight text-slate-900 sm:text-[26px]">
        ¿Qué quieres resolver hoy?
      </h2>
      <p className="mt-2 max-w-[600px] text-sm leading-6 text-slate-600 sm:text-base">
        {confirmableActions.length > 0
          ? 'Dime lo que pasó como se lo contarías a una persona. Puedo completar un registro, explicarte tus números y ayudarte a pensar una compra; nada se guarda sin que lo revises.'
          : 'Puedo explicarte tus números, decirte si una compra cabe en tu mes y ayudarte a pensarla con calma. Uso lo que ya registraste; no me conecto a tu banco ni muevo dinero.'}
      </p>
      {period && (
        <p className="mt-2 text-xs text-slate-600">Datos disponibles de: {formatPeriod(period)}</p>
      )}
      <div id={extraSuggestionsId} className="mt-5 grid gap-2.5 sm:grid-cols-2 sm:gap-3">
        {availableSuggestions.map((item, index) => {
          const usesConsultation =
            item.kind === 'analysis' || ('usesConsultation' in item && item.usesConsultation);
          const unavailable = usesConsultation && !canUseAnalysis;
          const unavailableReasonId = `assistant-suggestion-unavailable-${index}`;
          const hiddenOnMobile = index >= 3 && !showMoreSuggestions;
          return (
            <button
              type="button"
              key={item.label}
              onClick={() => {
                if (!unavailable) onPick(item);
              }}
              aria-disabled={unavailable || undefined}
              aria-describedby={unavailable ? unavailableReasonId : undefined}
              className={`tap-target min-h-[68px] items-start gap-3 rounded-[14px] border px-4 py-3 text-left shadow-sm sm:flex ${hiddenOnMobile ? 'hidden' : 'flex'} ${unavailable ? 'cursor-not-allowed border-slate-200 bg-slate-100' : 'border-[#E8E2D9] bg-white hover:border-[#D8CFC0] hover:bg-[#F6F2EC]'}`}
            >
              <span className="mt-0.5">
                <SuggestionIcon name={item.icon} />
              </span>
              <span>
                <strong className="block text-sm text-slate-900">{item.label}</strong>
                <span className="mt-0.5 block text-xs leading-5 text-slate-600">
                  {item.description}
                </span>
                <span
                  className={`mt-1 block text-[11px] font-semibold ${usesConsultation ? 'text-blue-700' : 'text-emerald-700'}`}
                >
                  {usesConsultation ? 'Usa 1 consulta' : 'No usa consulta'}
                </span>
                {unavailable && (
                  <span
                    id={unavailableReasonId}
                    className="mt-1 block text-xs font-medium leading-5 text-amber-800"
                  >
                    No disponible: no te quedan consultas de análisis.
                  </span>
                )}
              </span>
            </button>
          );
        })}
      </div>
      <button
        type="button"
        aria-expanded={showMoreSuggestions}
        aria-controls={extraSuggestionsId}
        onClick={() => setShowMoreSuggestions((value) => !value)}
        className="tap-target mt-2 inline-flex items-center rounded-xl px-2 text-sm font-semibold text-blue-700 hover:bg-blue-50 sm:hidden"
      >
        {showMoreSuggestions ? 'Ver menos ejemplos' : 'Ver más ejemplos'}
      </button>
      <p className="mt-3 text-xs leading-5 text-slate-600">
        Toca un ejemplo para llevarlo al mensaje. Puedes cambiar el producto, el monto o el plazo
        antes de enviarlo.
      </p>
    </div>
  );
}
function MessageBubble({
  message,
  proposal,
  ...interaction
}: { message: AssistantMessage; proposal?: AiActionProposal } & ProposalInteractionProps) {
  if (message.role === 'user')
    return (
      <article aria-label="Tu mensaje" className="flex justify-end">
        <div className="max-w-[90%] rounded-2xl rounded-br-sm bg-slate-900 px-3.5 py-2.5 text-sm leading-6 text-white sm:max-w-xl sm:px-4 sm:py-3">
          <FormattedMessage text={message.content} />
        </div>
      </article>
    );
  const helpDestination = getHelpDestination(message.helpAction);
  return (
    <article aria-label="Mensaje del asistente" className="max-w-[680px]">
      <div className="flex items-start gap-2 sm:gap-2.5">
        <AssistantAvatar />
        <div className="min-w-0 flex-1 space-y-2.5 sm:space-y-3">
          <div className="rounded-2xl rounded-tl-sm bg-white px-3.5 py-2.5 text-sm leading-6 text-slate-700 shadow-sm sm:px-4 sm:py-3">
            <FormattedMessage text={message.content} />
            {message.incomplete && <IncompleteNotice />}
            {helpDestination && (
              <Link
                className="button button-secondary mt-3 w-full sm:w-auto"
                to={helpDestination.deepLink}
              >
                Abrir {helpDestination.label}
              </Link>
            )}
          </div>
          {message.analysis && <AnalysisCard analysis={message.analysis} />}
        </div>
      </div>
      {proposal && (
        <div className="mt-2.5 sm:ml-[42px] sm:mt-3">
          <ProposalCard proposal={proposal} variant="inline" {...interaction} />
        </div>
      )}
    </article>
  );
}

function getHelpDestination(
  helpAction: AssistantMessage['helpAction'],
): { deepLink: AppHelpDeepLink; label: string } | null {
  if (
    !helpAction ||
    helpAction.type !== 'open_app_section' ||
    typeof helpAction.deepLink !== 'string' ||
    !Object.prototype.hasOwnProperty.call(APP_HELP_DESTINATIONS, helpAction.deepLink)
  )
    return null;

  const deepLink = helpAction.deepLink as AppHelpDeepLink;
  return { deepLink, label: APP_HELP_DESTINATIONS[deepLink] };
}
function AssistantAvatar() {
  return (
    <span
      className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-[9px] bg-slate-900 text-xs text-emerald-300 sm:h-8 sm:w-8 sm:rounded-[10px] sm:text-sm"
      aria-hidden
    >
      ✦
    </span>
  );
}
function AssistantLoading() {
  return (
    <div className="flex max-w-[680px] items-start gap-2 sm:gap-2.5">
      <AssistantAvatar />
      <div className="flex-1 rounded-2xl rounded-tl-sm border border-slate-200 bg-white p-3.5 shadow-sm sm:p-4">
        <div className="flex items-center gap-2">
          <span
            className="h-4 w-4 animate-spin rounded-full border-2 border-slate-200 border-t-slate-900 motion-reduce:animate-none"
            aria-hidden
          />
          <span role="status" className="text-sm font-semibold text-slate-700">
            Revisando tus datos registrados…
          </span>
        </div>
        <div className="mt-3 space-y-2" aria-hidden>
          <span className="block h-2.5 w-[92%] rounded-full bg-slate-100" />
          <span className="block h-2.5 w-[78%] rounded-full bg-slate-100" />
          <span className="block h-2.5 w-[54%] rounded-full bg-slate-100" />
        </div>
      </div>
    </div>
  );
}
function IncompleteNotice() {
  return (
    <div className="mt-3 flex gap-2 rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs leading-5 text-amber-900">
      <InfoIcon />
      <span>
        <strong className="block">Respuesta incompleta</strong>Faltan datos registrados para
        confirmar esta parte de la respuesta.
      </span>
    </div>
  );
}
function AnalysisCard({ analysis }: { analysis: AssistantAnalysisCard }) {
  const provenance = {
    registered: { label: 'Registrado', className: 'bg-green-50 text-green-700' },
    projection: { label: 'Estimado', className: 'bg-blue-50 text-blue-700' },
    incomplete: { label: 'Información incompleta', className: 'bg-amber-50 text-amber-800' },
  } as const;
  const sources = [...new Set(analysis.metrics.map((metric) => metric.provenance))];
  return (
    <section
      className="overflow-hidden rounded-[14px] border border-slate-200 bg-white shadow-sm"
      aria-label={analysis.title}
    >
      <header className="flex items-center justify-between gap-3 border-b border-slate-100 bg-slate-50 px-3.5 py-3">
        <h3 className="text-sm font-bold text-slate-900">{analysis.title}</h3>
        <span
          className={`shrink-0 rounded-md px-2 py-1 text-[10px] font-bold uppercase tracking-wide ${analysis.dataCoverage === 'incomplete' ? provenance.incomplete.className : provenance.registered.className}`}
        >
          {analysis.dataCoverage === 'incomplete' ? 'Incompleta' : 'Datos registrados'}
        </span>
      </header>
      <div className="p-3.5">
        <p className="text-xs leading-5 text-slate-500">
          Datos de {formatPeriod(analysis.period)} · actualizados al{' '}
          {formatShortDate(analysis.asOf)}
        </p>
        <div className="mt-3 grid grid-cols-2 gap-2">
          {analysis.metrics.map((metric) => (
            <div
              key={metric.label}
              className={`rounded-xl p-2.5 ${metric.provenance === 'projection' ? 'bg-blue-50' : metric.provenance === 'incomplete' ? 'bg-amber-50' : 'bg-slate-50'}`}
            >
              <p className="text-[10px] font-bold uppercase tracking-wide text-slate-500">
                {metric.label}
              </p>
              <p className="mt-1 text-base font-bold tracking-tight tabular-nums text-slate-900">
                {formatMoney(metric.value, analysis.currency)}
              </p>
            </div>
          ))}
        </div>
        <div className="mt-3 flex flex-wrap gap-1.5">
          {sources.map((source) => (
            <span
              key={source}
              className={`rounded-md px-2 py-1 text-[11px] font-bold ${provenance[source].className}`}
            >
              {provenance[source].label}
            </span>
          ))}
        </div>
        {analysis.notice && (
          <div className="mt-3 flex gap-2 rounded-xl bg-amber-50 p-2.5 text-xs leading-5 text-amber-900">
            <InfoIcon />
            <span>{analysis.notice}</span>
          </div>
        )}
      </div>
    </section>
  );
}
function OfflineNotice() {
  return (
    <div
      role="status"
      className="flex max-w-xl items-center gap-3 rounded-2xl border border-slate-200 bg-slate-100 p-4"
    >
      <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-[10px] bg-white text-slate-600">
        <OfflineIcon />
      </span>
      <span>
        <strong className="block text-sm text-slate-900">Estás sin conexión</strong>
        <span className="text-sm leading-5 text-slate-600">
          Tu mensaje se queda escrito y lo envías cuando vuelva el internet.
        </span>
      </span>
    </div>
  );
}
function InlineMarkdown({ text }: { text: string }) {
  return (
    <>
      {text
        .split(/(\*\*[^*]+\*\*)/g)
        .map((part, index) =>
          part.startsWith('**') && part.endsWith('**') ? (
            <strong key={index}>{part.slice(2, -2)}</strong>
          ) : (
            part
          ),
        )}
    </>
  );
}
function FormattedMessage({ text }: { text: string }) {
  const blocks = text.split(/\n\s*\n/).filter(Boolean);
  return (
    <>
      {blocks.map((block, index) => {
        const lines = block.split('\n');
        const list = lines.every((line) => /^(?:[-•]|\d+\.)\s+/.test(line));
        if (list)
          return (
            <ul key={index} className="my-2 list-disc space-y-1 pl-5">
              {lines.map((line, lineIndex) => (
                <li key={lineIndex}>
                  <InlineMarkdown text={line.replace(/^(?:[-•]|\d+\.)\s+/, '')} />
                </li>
              ))}
            </ul>
          );
        return (
          <p key={index} className={index ? 'mt-3 whitespace-pre-wrap' : 'whitespace-pre-wrap'}>
            <InlineMarkdown text={block} />
          </p>
        );
      })}
    </>
  );
}
function Status({ tone, text }: { tone: 'warning'; text: string }) {
  return (
    <p
      role="status"
      className={
        tone === 'warning'
          ? 'rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900'
          : ''
      }
    >
      {text}
    </p>
  );
}
function CapabilityNotice({
  consentGranted,
  expiredTrialAt,
}: {
  consentGranted: boolean;
  expiredTrialAt?: string | null;
}) {
  return (
    <div
      role="status"
      className="max-w-2xl rounded-2xl border border-slate-200 bg-white p-5 shadow-sm"
    >
      <p className="text-[11px] font-bold uppercase tracking-wider text-blue-700">Asistente Plus</p>
      <h2 className="mt-1 text-lg font-bold text-slate-900">
        {consentGranted && expiredTrialAt
          ? 'Tu prueba del asistente terminó'
          : consentGranted
            ? 'Convierte tus datos en decisiones más claras'
            : 'Conoce lo que puede hacer por ti'}
      </h2>
      <p className="mt-2 text-sm leading-6 text-slate-600">
        Revisa una compra con tus números y piensa con calma por qué la quieres antes de decidir. El
        asistente explica sus cuentas y nunca mueve dinero sin tu confirmación.
      </p>
      <div className="mt-4 grid gap-2 sm:grid-cols-2">
        {SUGGESTIONS.filter((item) => item.kind === 'analysis')
          .slice(0, 2)
          .map((item) => (
            <div
              key={item.label}
              className="flex items-start gap-3 rounded-xl border border-slate-200 bg-slate-50 p-3"
            >
              <SuggestionIcon name={item.icon} />
              <span>
                <strong className="block text-sm text-slate-900">{item.label}</strong>
                <span className="mt-0.5 block text-xs leading-5 text-slate-600">
                  {item.description}
                </span>
                <span className="mt-1 block text-[11px] font-semibold text-blue-700">
                  Usa 1 consulta
                </span>
              </span>
            </div>
          ))}
      </div>
      {consentGranted ? (
        expiredTrialAt ? (
          <p className="mt-4 text-sm leading-6 text-slate-600">
            Tu prueba estuvo vigente hasta el <ShortDate value={expiredTrialAt} />. Para volver a
            usar el asistente necesitas acceso a Plus. Plus todavía no se puede activar desde la
            app; tus registros y las demás herramientas siguen disponibles.
          </p>
        ) : (
          <p className="mt-4 text-sm leading-6 text-slate-600">
            Tu acceso a Plus no está activo en este momento. Plus todavía no se puede activar desde
            la app; puedes seguir usando las demás herramientas.
          </p>
        )
      ) : (
        <p className="mt-4 text-sm leading-6 text-slate-600">
          Para probarlo, primero revisa y autoriza el uso de IA. Puedes cambiar esta decisión
          después.
        </p>
      )}
    </div>
  );
}
function AssistantAccessAction({
  consentGranted,
  expiredTrialAt,
  loading,
  loadFailed,
  onRetry,
}: {
  consentGranted: boolean;
  expiredTrialAt?: string | null;
  loading: boolean;
  loadFailed: boolean;
  onRetry: () => void;
}) {
  return (
    <div
      data-testid="assistant-access-action"
      className="shrink-0 border-t border-[#E8E2D9] bg-[#FAF8F5]/95 px-3 py-3 backdrop-blur sm:px-5 lg:px-10 lg:pb-4"
    >
      <div className="mx-auto flex max-w-2xl flex-col gap-3 rounded-2xl border border-slate-200 bg-white p-3 shadow-sm sm:flex-row sm:items-center sm:justify-between sm:px-4">
        {loading ? (
          <p role="status" className="text-sm font-semibold text-slate-700">
            Comprobando tu acceso al asistente…
          </p>
        ) : loadFailed ? (
          <>
            <div role="alert">
              <h2 className="text-sm font-bold text-slate-900">No pudimos comprobar tu acceso</h2>
              <p className="mt-1 text-sm leading-5 text-slate-600">
                Puede ser un problema de conexión. No significa que hayas perdido Plus ni tus
                consultas. El campo para escribir seguirá cerrado hasta confirmarlo.
              </p>
            </div>
            <button type="button" onClick={onRetry} className="button button-secondary shrink-0">
              Volver a comprobar
            </button>
          </>
        ) : consentGranted ? (
          <>
            <p className="text-sm leading-5 text-slate-600">
              {expiredTrialAt ? (
                <>
                  <strong className="text-slate-900">
                    Tu prueba terminó el <ShortDate value={expiredTrialAt} />.
                  </strong>{' '}
                  Plus aún no se puede activar desde la app; puedes escribirnos para conocer su
                  disponibilidad.
                </>
              ) : (
                <>
                  <strong className="text-slate-900">Plus aún no tiene activación pública.</strong>{' '}
                  Si quieres conocer su disponibilidad, puedes escribirnos.
                </>
              )}
            </p>
            <Link className="button button-secondary shrink-0" to="/soporte">
              Contactar a Soporte
            </Link>
          </>
        ) : (
          <>
            <p className="text-sm leading-5 text-slate-600">
              <strong className="text-slate-900">Primero revisa el permiso de IA.</strong> La prueba
              se ofrece una sola vez por cuenta. Si no la has usado, empieza cuando guardas el
              permiso.
            </p>
            <Link className="button button-primary shrink-0" to="/ajustes#asistente">
              Revisar permiso de IA
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
function AccessNotice({
  error,
  onRetry,
  canRetry,
}: {
  error: unknown;
  onRetry: () => void;
  canRetry: boolean;
}) {
  const status = error instanceof AxiosError ? error.response?.status : undefined;
  const accessBlocked = status === 403;
  const message = getApiErrorMessage(error, 'No pudimos confirmar tu acceso al asistente.');
  const needsConsent = /consentimiento/i.test(message);
  return (
    <div
      role="alert"
      className={`flex max-w-xl gap-3 rounded-2xl border p-4 ${accessBlocked ? 'border-slate-200 bg-white' : 'border-red-200 bg-white'}`}
    >
      <span
        className={`inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-[10px] ${accessBlocked ? 'bg-slate-100 text-slate-600' : 'bg-red-50 text-red-600'}`}
      >
        <AlertIcon />
      </span>
      <div>
        <h2 className={`text-sm font-bold ${accessBlocked ? 'text-slate-900' : 'text-red-800'}`}>
          {accessBlocked ? 'El asistente requiere acceso activo' : 'No pude responder ahorita'}
        </h2>
        <p
          className={`mt-1 text-sm leading-6 ${accessBlocked ? 'text-slate-600' : 'text-red-900'}`}
        >
          {accessBlocked
            ? message
            : getApiErrorMessage(
                error,
                'Algo falló de nuestro lado. Tu consulta no se cobró y tus datos no se modificaron.',
              )}
        </p>
        {accessBlocked && needsConsent ? (
          <Link
            className="tap-target mt-3 inline-flex items-center text-sm font-semibold text-blue-700 underline"
            to="/ajustes#asistente"
          >
            Abrir Ajustes
          </Link>
        ) : (
          !accessBlocked && (
            <button
              type="button"
              onClick={onRetry}
              disabled={!canRetry}
              className="tap-target mt-3 inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-900 hover:bg-slate-50 disabled:cursor-not-allowed disabled:text-slate-400"
            >
              <RetryIcon />
              Volver a intentar
            </button>
          )
        )}
      </div>
    </div>
  );
}
function formatPeriod(month: string) {
  const [year, number] = month.split('-').map(Number);
  const name = new Intl.DateTimeFormat('es-MX', { month: 'long', year: 'numeric' }).format(
    new Date(year, number - 1, 1),
  );
  return name.charAt(0).toUpperCase() + name.slice(1);
}
function formatShortDate(value: string) {
  return new Intl.DateTimeFormat('es-MX', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }).format(new Date(value));
}
function ShortDate({ value }: { value: string }) {
  return <time dateTime={value}>{formatShortDate(value)}</time>;
}
function SuggestionIcon({ name }: { name: (typeof SUGGESTIONS)[number]['icon'] }) {
  const paths = {
    card: (
      <>
        <rect x="3" y="5" width="18" height="14" rx="3" />
        <path d="M3 10h18M7 15h3" />
      </>
    ),
    wallet: (
      <>
        <path d="M3.5 7.5A2.5 2.5 0 0 1 6 5h11.5v2.5M3.5 7.5V18A2.5 2.5 0 0 0 6 20.5h13V7.5H6a2.5 2.5 0 0 1-2.5-2.5" />
        <circle cx="15.5" cy="14" r="1.2" />
      </>
    ),
    target: (
      <>
        <circle cx="12" cy="12" r="8.5" />
        <circle cx="12" cy="12" r="4.5" />
        <circle cx="12" cy="12" r="1" />
      </>
    ),
    calendar: (
      <>
        <rect x="3" y="5" width="18" height="16" rx="2.5" />
        <path d="M3 10h18M8 3v4m8-4v4" />
      </>
    ),
  };
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-[18px] w-[18px] shrink-0 text-slate-500"
    >
      {paths[name]}
    </svg>
  );
}
function AlertIcon() {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      className="h-[17px] w-[17px]"
    >
      <path d="M12 3.5 21 20.5H3L12 3.5Z" />
      <path d="M12 9v4.5m0 3h.01" />
    </svg>
  );
}
function InfoIcon() {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.9"
      className="mt-0.5 h-4 w-4 shrink-0"
    >
      <circle cx="12" cy="12" r="8.5" />
      <path d="M12 11v5m0-8h.01" />
    </svg>
  );
}
function OfflineIcon() {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      className="h-[17px] w-[17px]"
    >
      <path d="m4 4 16 16M7 10.5A7 7 0 0 1 12 8.5c3.3 0 6.1 2.2 7 5.2M4.5 13.7c.4-1.2 1.1-2.2 2.1-3M9.2 17.2c.7-.5 1.7-.5 2.4 0" />
    </svg>
  );
}
function RetryIcon() {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      className="h-4 w-4"
    >
      <path d="M20 12a8 8 0 1 1-2.6-5.9M20 4.5V10h-5.5" />
    </svg>
  );
}
